import * as THREE from "three";
import {
  buildAdjacency,
  computeStats,
  createIcosphere,
  type IndexedMesh,
  InsideTest,
  meshSdf,
  type MeshStats,
  normalizeVolume,
  orientOutward,
} from "./geometry.ts";
import { remesh } from "./remesh.ts";

export type Tool = "grab" | "push" | "inflate" | "smooth" | "flatten";

export interface Hit {
  point: THREE.Vector3;
  normal: THREE.Vector3;
}

/**
 * A hole looked through: a line from the camera (the pointer's ray) that runs through a
 * hole of the clay without touching it (see Clay.holeAt).
 */
export interface Hole {
  /** The line through the hole, from the camera. */
  line: THREE.Ray;
  /** Where the hole is: on the line, amid its rim. */
  point: THREE.Vector3;
  /** The middle of the hole's outline, seen along the line: the centroid of what it rings. */
  middle: THREE.Vector3;
  /** How far the outline is from `middle`, on average. */
  size: number;
  /** The hole's rim: the vertices nearest the line each way round it, seen along it. */
  rim: Uint32Array;
  /**
   * How far the rim is from the line each way round, in HOLE_SECTORS (see sectorOf);
   * Infinity where no vertex was found.
   */
  outline: Float32Array;
}

/**
 * Starting clay: a lumpy ball (no holes), a lumpy donut (one hole), or two, three or four
 * rings fused (two, three or four holes).
 */
export type ClayKind = "blob" | "donut" | "pretzel" | "trefoil" | "quatrefoil";
/** The way a clay's holes go through: toward the camera (z), sideways (x) or upright (y). */
export type HoleFacing = "front" | "side" | "up";

/** Icosphere subdivision for the blob (13,692 vertices before remeshing). */
const BLOB_DETAIL = 36;
/** Donut grid: segments around the ring × around the tube (remeshed to even it out). */
const DONUT_SEGMENTS = [200, 64];
/**
 * Target edge length kept by remeshing. The clay gains or loses vertices as its surface
 * grows or shrinks, so big lumps stay as smooth as the rest.
 */
const EDGE_LENGTH = 0.035;
/** Remeshing passes applied to a freshly built starting clay. */
const INITIAL_REMESH_PASSES = 3;
/** Stroke steps between remeshing checks while a stroke is going on. */
const REMESH_INTERVAL = 8;
/**
 * During a stroke, only remesh once some edge is this many times too long (or short);
 * the stroke end evens everything out at the normal thresholds.
 */
const STROKE_REMESH_TRIGGER = 2;
/** Clay volume is conserved at that of a unit sphere. */
export const CLAY_VOLUME = (4 / 3) * Math.PI;

// Display spring: the visible surface chases the plastic shape with a slightly
// under-damped spring, which is where the "むにっ" lag and wobble come from.
const SPRING_K = 520;
const SPRING_C = 13;
/**
 * Inward velocity kick applied to the visible surface on touch, in brush radii per
 * second. It does not change the shape; it is only the "むにっ" of the first contact.
 */
const PRESS_IMPULSE = 3.5;
/** Push / inflate depth per second, in brush radii. */
const PUSH_SPEED = 1.5;
/** Fraction of the remaining distance to the flatten plane closed per second. */
const FLATTEN_RATE = 10;
/**
 * Laplacian smoothing passes per second. One pass only diffuses about one edge length,
 * so a dense mesh needs many to visibly even out a thumb-sized dent.
 */
const SMOOTH_PASSES_PER_SECOND = 420;
/** Tangential relaxation per frame inside the brush; keeps triangles from collapsing. */
const RELAX = 0.7;
/**
 * Tangential relaxation per frame everywhere else, so vertices flow in from the rest of
 * the body toward a stretched region before remeshing catches up.
 */
const RELAX_GLOBAL = 0.3;
/** A vertex standing out of its one-ring by more than this × mean edge length is a spike. */
const SPIKE_RATIO = 0.35;
/** Smoothing passes applied around detected spikes and folded triangles. */
const REPAIR_PASSES = 3;
/** Extra smoothing across the surface while grabbing or inflating, so lumps stay rounded. */
const ROUNDING = 0.3;
/** Fraction of the brush radius at full strength for inflate / flatten (a thumb, not a needle). */
const PLATEAU = 0.3;
/**
 * How deep grab takes hold, in brush radii: a ball of clay up to this big, touching the
 * surface under the pointer and reaching halfway through the part there, is held whole,
 * and the hold weakens around it. A tube or handle then comes along as a whole instead of
 * its front sliding over its back into a flap or a beak, while on a broad face the hold
 * spreads over the brush radius much as a plain brush would.
 */
const GRAB_DEPTH = 1;
/**
 * How the grab's hold spreads as the pull gets longer: the falloff around the held core
 * reaches at least this many times the distance pulled so far, so a long pull drags a
 * broad stretch of clay along instead of drawing a narrow neck or a creased point.
 */
const GRAB_SPREAD = 1;
/** The furthest the grab's falloff may spread, in brush radii. */
const GRAB_MAX_REACH = 2;
/**
 * Pushed back into the clay (against the surface where it was taken hold of), the hold
 * spreads this many times the distance pushed in, up to GRAB_MAX_PUSH_REACH brush radii:
 * the clay behind it (the arms either side of a handle's point, pulled down between them)
 * is shortened over a long stretch instead of buckling into folds right behind the hold.
 */
const GRAB_PUSH_SPREAD = 3;
const GRAB_MAX_PUSH_REACH = 4;
/** Largest grab movement per frame in brush radii, so a pointer jump cannot tear the surface. */
const MAX_GRAB_STEP = 0.5;
/**
 * A grab draws the clay out freely until a part it stretches is thinner than SEPARATE_BELOW
 * of MIN_THICKNESS, or than it was when the grab took hold if that was thinner still, so
 * walls left thin by earlier strokes do not hold up a pull that leaves them as they are.
 * From there the pull slows, to a stop this much of MIN_THICKNESS thinner (never thinner
 * than STRETCH_FLOOR of it), and what it thinned is pushed back out when the grab lets go
 * (see RELEASE_STEPS).
 */
const STRETCH_SLACK = 0.3;
const STRETCH_FLOOR = 0.5;
/**
 * Stroke steps of mending when a grab lets go: the walls it thinned are pushed back out,
 * the clay for it taken from all round the piece, and the display spring shows the clay
 * giving a little.
 */
const RELEASE_STEPS = 3;
/**
 * Thinnest the clay may get: pressing and volume refills stop at walls, tubes and tips
 * this thin, and whatever still gets well thinner (a pull, smoothing) is pushed back
 * apart, so a press cannot go through and a pull cannot draw out a needle.
 */
const MIN_THICKNESS = 0.16;
/**
 * Narrowest gap between faces of the clay turned toward each other (a deep crease, two
 * swelling lumps meeting, the two sides of a hole), so the surface never passes through
 * itself, and a hole squeezed as far as it goes stays open wide enough to be seen and
 * pulled open again.
 */
const MIN_GAP = 0.15;
// Walls and gaps are measured up to these; closer in, tools and volume refills slow down.
// Rounded to float32 so they compare equal to themselves once stored in a Float32Array.
const THICK_ENOUGH = Math.fround(1.5 * MIN_THICKNESS);
const WIDE_ENOUGH = Math.fround(2 * MIN_GAP);
/**
 * Farthest one flatten step moves a vertex. Far from the plane, a step would otherwise
 * cross a whole wall between one measure of it and the next; this short, it comes into the
 * measured range and is slowed there before it can.
 */
const FLATTEN_STEP = MIN_THICKNESS / 2;
/** Ring-averaging passes over the room to move (see updateRoom). */
const ROOM_SMOOTH_PASSES = 2;
/** Every vertex's walls and gaps are re-measured at least once in this many stroke steps. */
const SEPARATION_SWEEP = 8;
/** Grid cell for finding the facing side of a wall or gap, and for inside tests. */
const SEPARATION_CELL = MIN_THICKNESS / 2;
/**
 * Walls and gaps checked for crossings per stroke step (see measureSeparation). Beyond
 * this (a badly tangled lump), the rest keep their last verdict and take turns.
 */
const INSIDE_TESTS = 1500;
/**
 * Brush vertices turned toward the brush at least this much (the cosine) are worked in full;
 * the brush spreads over the surface only through them (see keepConnected).
 */
const FACING_SPREAD = 0.3;
/** Normals at least this opposed belong to the two sides of one wall or gap. */
const OPPOSED = -0.5;
/**
 * Separating only restores walls and gaps that fell below this fraction of their minimum;
 * nearer the minimum, the brakes on tools and refills hold them. Pushing apart right at
 * the minimum would fight the brakes every step and slowly wrinkle thin parts.
 */
const SEPARATE_BELOW = 0.8;
/** Fraction of a too thin wall's or too narrow gap's shortfall restored per stroke step. */
const SEPARATE_RATE = 0.5;
/**
 * Largest push per stroke step when separating, in edge lengths. Bigger pushes roughen the
 * surface faster than relaxing evens it out, and a rough surface looks thin everywhere.
 */
const MAX_SEPARATE_STEP = 0.3;
/**
 * Smoothing applied where walls and gaps are pushed apart. Their facing sides are found
 * vertex by vertex, so the pushes differ a little from one vertex to the next; without
 * smoothing those differences grow into wrinkles.
 */
const SEPARATE_SMOOTH = 0.3;
/**
 * Largest move per stroke step when restoring the volume, in edge lengths. Without it, the
 * whole correction would pile up on the few places still free once most of the clay is
 * thin, and thin them faster than they are measured.
 */
const MAX_REFILL_STEP = 0.2;
/**
 * How far a vertex the volume refill moves is rounded toward its one-ring across the
 * surface: fully once moved this many times less than EDGE_LENGTH in a step. Higher keeps
 * the refilled clay smoother, and wears away more of the detail made before.
 */
const REFILL_ROUNDING = 10;
/**
 * The wheel works a ring of dabs around the upright axis, at most this far apart (in brush
 * radii) so they press one even ring, and this many at the fewest and the most.
 */
const WHEEL_SPACING = 0.7;
const WHEEL_DABS = { min: 6, max: 32 };
/** Ways round a hole its rim is looked for in (see Clay.holeAt). */
const HOLE_SECTORS = 64;
/** Rim vertices a push on a hole gives back its clay around, one in so many (see workHole). */
const RIM_REFILL_EVERY = 4;
/** How far back from a hole's outline stands for "out of reach" (see beyondRim). */
const FAR = 1e6;
/** How strongly the brush's tint shows where the brush works at full strength. */
const TINT_ALPHA = 0.55;
/**
 * The tint is brighter than its colour (past white, before the screen clips it), so it
 * glows on the clay: covering the clay's own colour, it would look painted on.
 */
const TINT_GLOW = 1.8;

function falloff(t: number): number {
  if (t >= 1) return 0;
  const u = 1 - t * t;
  return u * u * u;
}

function plateauFalloff(t: number): number {
  return t <= PLATEAU ? 1 : falloff((t - PLATEAU) / (1 - PLATEAU));
}

function smoothstep(e0: number, e1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - e0) / (e1 - e0)));
  return t * t * (3 - 2 * t);
}

/** Hash-table slot of the separation grid cell containing a point. */
function cellSlot(x: number, y: number, z: number, mask: number): number {
  const ix = Math.floor(x / SEPARATION_CELL), iy = Math.floor(y / SEPARATION_CELL);
  const iz = Math.floor(z / SEPARATION_CELL);
  return (Math.imul(ix, 73856093) ^ Math.imul(iy, 19349663) ^ Math.imul(iz, 83492791)) & mask;
}

function createBlob(): IndexedMesh {
  const mesh = createIcosphere(BLOB_DETAIL);
  const p = mesh.positions;
  for (let i = 0; i < p.length; i += 3) {
    const x = p[i], y = p[i + 1], z = p[i + 2];
    const r = 1 +
      0.07 * Math.sin(2.3 * x + 0.4) * Math.sin(1.9 * y + 1.3) +
      0.05 * Math.sin(3.1 * z + 2.2 * x + 0.7) +
      0.04 * Math.cos(2.7 * y - 1.6 * z);
    p[i] = x * r;
    p[i + 1] = y * r * 0.9;
    p[i + 2] = z * r;
  }
  return mesh;
}

/** A donut standing upright with its hole facing the camera. */
function createDonut(): IndexedMesh {
  const [nu, nv] = DONUT_SEGMENTS, major = 1.2, minor = 0.42;
  const positions = new Float32Array(nu * nv * 3);
  const index = new Uint32Array(nu * nv * 6);
  for (let i = 0; i < nu; i++) {
    const u = (2 * Math.PI * i) / nu, i1 = (i + 1) % nu;
    for (let j = 0; j < nv; j++) {
      const v = (2 * Math.PI * j) / nv, j1 = (j + 1) % nv;
      const r = minor * (1 + 0.08 * Math.sin(3 * u + 0.5) + 0.05 * Math.sin(2 * u + v + 1.3));
      const o = (i * nv + j) * 3;
      positions[o] = (major + r * Math.cos(v)) * Math.cos(u);
      positions[o + 1] = (major + r * Math.cos(v)) * Math.sin(u);
      positions[o + 2] = r * Math.sin(v);
      const a = i * nv + j, b = i1 * nv + j, c = i1 * nv + j1, d = i * nv + j1;
      index.set([a, b, c, a, c, d], (i * nv + j) * 6);
    }
  }
  const mesh = { positions, index };
  orientOutward(mesh);
  return mesh;
}

/** Two donuts side by side, fused into one piece with two holes facing the camera. */
function createPretzel(): IndexedMesh {
  const ring = (cx: number, x: number, y: number, z: number) =>
    Math.hypot(Math.hypot(x - cx, y) - 0.55, z) - 0.3;
  return meshSdf(
    (x, y, z) => {
      const a = ring(-0.62, x, y, z), b = ring(0.62, x, y, z), k = 0.2;
      const h = Math.max(k - Math.abs(a - b), 0) / k;
      return Math.min(a, b) - h * h * k / 4;
    },
    1.7,
    EDGE_LENGTH,
  );
}

/** Three donuts in a triangle, two above and one below, fused into one piece with three holes. */
function createTrefoil(): IndexedMesh {
  const ring = (cx: number, cy: number, x: number, y: number, z: number) =>
    Math.hypot(Math.hypot(x - cx, y - cy) - 0.55, z) - 0.3;
  const smin = (a: number, b: number, k = 0.2) => {
    const h = Math.max(k - Math.abs(a - b), 0) / k;
    return Math.min(a, b) - h * h * k / 4;
  };
  return meshSdf(
    (x, y, z) =>
      smin(smin(ring(-0.62, 0.36, x, y, z), ring(0.62, 0.36, x, y, z)), ring(0, -0.72, x, y, z)),
    1.7,
    EDGE_LENGTH,
  );
}

/** Four donuts in a square, two above and two below, fused into one piece with four holes. */
function createQuatrefoil(): IndexedMesh {
  const ring = (cx: number, cy: number, x: number, y: number, z: number) =>
    Math.hypot(Math.hypot(x - cx, y - cy) - 0.55, z) - 0.3;
  const smin = (a: number, b: number, k = 0.2) => {
    const h = Math.max(k - Math.abs(a - b), 0) / k;
    return Math.min(a, b) - h * h * k / 4;
  };
  return meshSdf(
    (x, y, z) =>
      smin(
        smin(ring(-0.62, 0.62, x, y, z), ring(0.62, 0.62, x, y, z)),
        smin(ring(-0.62, -0.62, x, y, z), ring(0.62, -0.62, x, y, z)),
      ),
    1.7,
    EDGE_LENGTH,
  );
}

/**
 * The starting clay of a kind: evenly meshed at the remeshing edge length, at the
 * conserved volume and centred on the origin. Its holes, made facing the camera, are
 * turned a quarter round to face `holes`: round the upright axis to face sideways, or
 * round the left-right axis (the clay laid down) to face up.
 */
export function createClayMesh(kind: ClayKind, holes: HoleFacing = "front"): IndexedMesh {
  let mesh = kind === "blob"
    ? createBlob()
    : kind === "donut"
    ? createDonut()
    : kind === "pretzel"
    ? createPretzel()
    : kind === "trefoil"
    ? createTrefoil()
    : createQuatrefoil();
  for (let pass = 0; pass < INITIAL_REMESH_PASSES; pass++) {
    mesh = remesh(mesh.positions, mesh.index, [], EDGE_LENGTH) ?? mesh;
  }
  const p = mesh.positions;
  normalizeVolume(p, mesh.index, CLAY_VOLUME);
  const n = p.length / 3;
  const { centroid } = computeStats(p, mesh.index, new Float32Array(n * 3), new Float32Array(n));
  for (let i = 0; i < p.length; i += 3) {
    const x = p[i] - centroid.x, y = p[i + 1] - centroid.y, z = p[i + 2] - centroid.z;
    p[i] = holes === "side" ? z : x;
    p[i + 1] = holes === "up" ? z : y;
    p[i + 2] = holes === "side" ? -x : holes === "up" ? -y : z;
  }
  return { positions: p, index: mesh.index };
}

/** A kneaded shape: positions with the mesh they belong to (remeshing changes it). */
export interface ClaySnapshot {
  rest: Float32Array;
  index: Uint32Array;
}

/** Where a stroke step's volume change is given back (see preserveVolume). */
type Refill = "body" | "brush" | THREE.Vector3[];

/**
 * How a stroke repeats itself: mirrored across the plane through the origin with normal
 * `mirror` (symmetry mode), or turned all the way round the upright axis (the wheel).
 */
export type Repeat = { mirror: number } | "wheel" | null;

/**
 * The transforms (through the origin) carrying the dab at `hit` to each dab worked with it,
 * the first the identity.
 */
function copiesOf(repeat: Repeat, hit: Hit, radius: number): THREE.Matrix4[] {
  const identity = new THREE.Matrix4();
  if (repeat === null) return [identity];
  if (repeat !== "wheel") {
    const flip = [1, 1, 1];
    flip[repeat.mirror] = -1;
    return [identity, new THREE.Matrix4().makeScale(flip[0], flip[1], flip[2])];
  }
  const around = 2 * Math.PI * Math.hypot(hit.point.x, hit.point.z);
  const count = Math.min(
    WHEEL_DABS.max,
    Math.max(WHEEL_DABS.min, Math.ceil(around / (WHEEL_SPACING * radius))),
  );
  return Array.from(
    { length: count },
    (_, k) => new THREE.Matrix4().makeRotationY((2 * Math.PI * k) / count),
  );
}

/** The dab at `hit` carried by each of `copies` (see copiesOf). */
function dabsOf(hit: Hit, copies: THREE.Matrix4[]): Hit[] {
  return copies.map((m, d) =>
    d === 0 ? hit : {
      point: hit.point.clone().applyMatrix4(m),
      normal: hit.normal.clone().applyMatrix4(m),
    }
  );
}

/** The unit direction from the upright axis toward `p` (in x and z), or null on the axis. */
function sideOf(p: THREE.Vector3): Side | null {
  const len = Math.hypot(p.x, p.z);
  return len < 1e-3 ? null : { x: p.x / len, z: p.z / len };
}

/** A side of the wheel's axis: a unit direction in x and z. */
interface Side {
  x: number;
  z: number;
}

const _turn = { c: 1, s: 0 };

/**
 * The turn about the upright axis from `side` round to the side vertex `o` (an offset into
 * `p`) is on, as its cosine and sine: (x, z) turns to (c x + s z, -s x + c z).
 */
function turnTo(side: Side, p: Float32Array, o: number): { c: number; s: number } {
  const x = p[o], z = p[o + 2], len = Math.hypot(x, z);
  if (len < 1e-6) {
    _turn.c = 1;
    _turn.s = 0;
  } else {
    _turn.c = (side.x * x + side.z * z) / len;
    _turn.s = (side.z * x - side.x * z) / len;
  }
  return _turn;
}

/** A dab's weights: the vertices it works, and how much. */
interface Footprint {
  index: Uint32Array;
  weight: Float32Array;
}

interface Stroke {
  tool: Tool;
  /** On a hole, mirrored only if the clay has a hole at the mirror image too (see holeDabs). */
  repeat: Repeat;
  /** Begun looking through a hole: the tool works the hole (see holeAt). */
  hole: boolean;
  /**
   * Transforms from the pointer's dab to each dab (see copiesOf). Fixed for a grab; the other
   * tools follow the pointer (the wheel's ring grows and shrinks with it).
   */
  copies: THREE.Matrix4[];
  /**
   * On the wheel, where the tool is held: the side of the axis the pointer is on. The clay
   * turns under it, so each vertex is worked as if it were there (see turnTo); a grab holds
   * the ring it took hold of (see beginStroke).
   */
  side: Side | null;
  /**
   * grab: camera-facing plane the pointer is projected onto.
   * flatten: the plane square to the view through the point first touched.
   */
  plane: THREE.Plane;
  /** grab: current grabbed point. push/smooth: last hit point. */
  point: THREE.Vector3;
  normal: THREE.Vector3;
  /**
   * grab: for each dab (the stroke, then its copies), how far each vertex lay beyond the
   * held core when the stroke began.
   */
  grabBeyond: Float32Array[];
  /** grab: how thick each vertex's wall was when the grab took hold (see stretchRoom). */
  stretchFrom: Float32Array;
  /** grab: the falloff distance for a short pull (see grabReach). */
  grabReach: number;
  /** grab: how far the pointer has pulled so far. */
  pulled: THREE.Vector3;
}

/** The same hit reflected across the mirror plane through the origin with normal `axis`. */
export function mirrorHit(hit: Hit, axis: number): Hit {
  const point = hit.point.clone(), normal = hit.normal.clone();
  point.setComponent(axis, -point.getComponent(axis));
  normal.setComponent(axis, -normal.getComponent(axis));
  return { point, normal };
}

/** The same ray reflected across the mirror plane through the origin with normal `axis`. */
function mirrorRay(ray: THREE.Ray, axis: number): THREE.Ray {
  const mirrored = ray.clone();
  mirrored.origin.setComponent(axis, -mirrored.origin.getComponent(axis));
  mirrored.direction.setComponent(axis, -mirrored.direction.getComponent(axis));
  return mirrored;
}

/** Two unit directions square to `d` and to each other, to measure angles round a line along it. */
function acrossLine(d: THREE.Vector3): [THREE.Vector3, THREE.Vector3] {
  const u = Math.abs(d.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
  u.addScaledVector(d, -u.dot(d)).normalize();
  return [u, new THREE.Vector3().crossVectors(d, u)];
}

/** The sector (see HOLE_SECTORS) an angle round a line (-π to π) falls in. */
function sectorOf(angle: number): number {
  return Math.min(HOLE_SECTORS - 1, Math.floor((angle + Math.PI) / (2 * Math.PI) * HOLE_SECTORS));
}

/** A hole's outline (see Hole.outline) at `angle`, between the sectors either side. */
function outlineAt(outline: Float32Array, angle: number): number {
  const t = (angle + Math.PI) / (2 * Math.PI) * HOLE_SECTORS - 0.5, k = Math.floor(t);
  const a = outline[(k + HOLE_SECTORS) % HOLE_SECTORS], b = outline[(k + 1) % HOLE_SECTORS];
  if (!Number.isFinite(a)) return b;
  if (!Number.isFinite(b)) return a;
  return a + (b - a) * (t - k);
}

/**
 * Into `out`, the unit direction out from the line through `origin` along `d`, square to
 * it, toward the point at offset `o` in `p`; returns the point's distance from the line.
 */
function awayFromLine(
  origin: THREE.Vector3,
  d: THREE.Vector3,
  p: Float32Array,
  o: number,
  out: THREE.Vector3,
): number {
  const x = p[o] - origin.x, y = p[o + 1] - origin.y, z = p[o + 2] - origin.z;
  const along = x * d.x + y * d.y + z * d.z;
  out.set(x - d.x * along, y - d.y * along, z - d.z * along);
  const far = out.length();
  if (far > 0) out.divideScalar(far);
  return far;
}

const _p = new THREE.Vector3();
const _delta = new THREE.Vector3();
const _out = new THREE.Vector3();

export class Clay {
  readonly mesh: THREE.Mesh;
  /**
   * Where the brush works (or would, if pressed here), tinted over the clay: a see-through
   * copy of its surface, a child of `mesh` (see showBrush).
   */
  readonly tint: THREE.Mesh;
  /** Starting (reset) shape. */
  readonly initialMesh: IndexedMesh;
  // The mesh itself changes with remeshing; these are replaced by setMesh().
  vertexCount = 0;
  index: Uint32Array = new Uint32Array(0);
  /** Plastic shape: what the player has kneaded. Scoring uses this. */
  rest: Float32Array = new Float32Array(0);
  restAreas: Float32Array = new Float32Array(0);
  restStats!: MeshStats;
  /** Incremented whenever `rest` changes. */
  version = 0;
  brushRadius = 0.45;

  private restNormals: Float32Array = new Float32Array(0);
  private display: Float32Array = new Float32Array(0);
  private displayNormals: Float32Array = new Float32Array(0);
  private displayAreas: Float32Array = new Float32Array(0);
  private velocity: Float32Array = new Float32Array(0);
  private weights: Float32Array = new Float32Array(0);
  /** 1 for vertices strokes have worked on since the last full remesh. */
  private touched: Float32Array = new Float32Array(0);
  private scratch: Float32Array = new Float32Array(0);
  /**
   * Distance through the clay along each inward normal, up to THICK_ENOUGH; negative where
   * the vertex has crossed into another part (see measureSeparation).
   */
  private thickness: Float32Array = new Float32Array(0);
  /**
   * Distance to a facing part of the surface along each outward normal, up to WIDE_ENOUGH;
   * negative where the clay between is inside out (see measureSeparation).
   */
  private gap: Float32Array = new Float32Array(0);
  private inside = new InsideTest();
  /** How freely each vertex may move inward (see updateRoom). */
  private roomIn: Float32Array = new Float32Array(0);
  /** How freely each vertex may move outward (see updateRoom). */
  private roomOut: Float32Array = new Float32Array(0);
  private separationPhase = 0;
  private insidePhase = 0;
  // Vertices bucketed by separation grid cell (hashed into slots): slot s holds
  // cellItems[cellStart[s]..cellStart[s+1]).
  private cellSlots: Uint32Array = new Uint32Array(0);
  private cellStart: Uint32Array = new Uint32Array(0);
  private cellItems: Uint32Array = new Uint32Array(0);
  private flags: Uint8Array = new Uint8Array(0);
  private flagged: Uint32Array = new Uint32Array(0);
  private active: Uint32Array = new Uint32Array(0);
  private activeCount = 0;
  private adjOffsets: Uint32Array = new Uint32Array(0);
  private adjNeighbors: Uint32Array = new Uint32Array(0);
  private stroke: Stroke | null = null;
  /** What the tint shows now, so an unchanged brush is not worked out again. */
  private tinted = "";
  private stepsSinceRemesh = 0;
  private awake = true;

  constructor(material: THREE.Material, initial: IndexedMesh) {
    this.initialMesh = initial;
    this.mesh = new THREE.Mesh(new THREE.BufferGeometry(), material);
    this.tint = new THREE.Mesh(
      new THREE.BufferGeometry(),
      new THREE.MeshBasicMaterial({
        color: new THREE.Color(TINT_GLOW, TINT_GLOW, TINT_GLOW),
        vertexColors: true,
        transparent: true,
        // Strength is applied before the screen clips the colour, so the glow survives it.
        premultipliedAlpha: true,
        depthWrite: false,
        // The same surface as the clay's: drawn over it, never under.
        polygonOffset: true,
        polygonOffsetFactor: -1,
        polygonOffsetUnits: -4,
        toneMapped: false,
      }),
    );
    this.tint.frustumCulled = false;
    this.tint.visible = false;
    this.mesh.add(this.tint);
    this.setMesh(initial.positions.slice(), initial.index.slice());
  }

  /**
   * Installs a mesh (after construction, reset or remeshing), carrying over the displayed
   * surface, its velocity, brush weights, the touched mask, walls and gaps when given.
   */
  private setMesh(
    rest: Float32Array,
    index: Uint32Array,
    display: Float32Array = rest.slice(),
    velocity: Float32Array = new Float32Array(rest.length),
    weights: Float32Array = new Float32Array(rest.length / 3),
    touched: Float32Array = new Float32Array(rest.length / 3),
    thickness: Float32Array = new Float32Array(rest.length / 3).fill(THICK_ENOUGH),
    gap: Float32Array = new Float32Array(rest.length / 3).fill(WIDE_ENOUGH),
  ): void {
    const n = rest.length / 3;
    this.vertexCount = n;
    this.index = index;
    this.rest = rest;
    this.restNormals = new Float32Array(n * 3);
    this.restAreas = new Float32Array(n);
    this.display = display;
    this.displayNormals = new Float32Array(n * 3);
    this.displayAreas = new Float32Array(n);
    this.velocity = velocity;
    this.weights = weights;
    this.touched = touched;
    this.scratch = new Float32Array(n * 3);
    this.thickness = thickness;
    this.gap = gap;
    this.roomIn = new Float32Array(n);
    this.roomOut = new Float32Array(n);
    this.cellSlots = new Uint32Array(n);
    const slots = 2 ** Math.ceil(Math.log2(2 * n));
    this.cellStart = new Uint32Array(slots + 1);
    this.cellItems = new Uint32Array(n);
    this.flags = new Uint8Array(n);
    this.flagged = new Uint32Array(n);
    this.active = new Uint32Array(n);
    this.activeCount = 0;
    for (let i = 0; i < n; i++) if (weights[i] > 0) this.active[this.activeCount++] = i;
    const adjacency = buildAdjacency(index, n);
    this.adjOffsets = adjacency.offsets;
    this.adjNeighbors = adjacency.neighbors;

    this.restStats = computeStats(rest, index, this.restNormals, this.restAreas);
    computeStats(display, index, this.displayNormals, this.displayAreas);
    this.updateRoom();

    const geometry = new THREE.BufferGeometry();
    geometry.setIndex(new THREE.BufferAttribute(index, 1));
    geometry.setAttribute(
      "position",
      new THREE.BufferAttribute(display, 3).setUsage(THREE.DynamicDrawUsage),
    );
    geometry.setAttribute(
      "normal",
      new THREE.BufferAttribute(this.displayNormals, 3).setUsage(THREE.DynamicDrawUsage),
    );
    geometry.computeBoundingSphere();
    const old = this.mesh.geometry;
    this.mesh.geometry = geometry;
    old.dispose();
    // The tint shares the surface, with a colour and strength for each vertex.
    const tint = new THREE.BufferGeometry();
    tint.setIndex(geometry.index);
    tint.setAttribute("position", geometry.getAttribute("position"));
    tint.setAttribute(
      "color",
      new THREE.BufferAttribute(new Float32Array(n * 4), 4).setUsage(THREE.DynamicDrawUsage),
    );
    this.tint.geometry.dispose();
    this.tint.geometry = tint;
    this.tinted = "";
  }

  /**
   * Re-triangulates where strokes have stretched or squeezed edges out of shape: long
   * edges split, short ones collapse (see remesh.ts). `trigger` lets small deviations
   * wait. Parts a stroke only carried along keep their triangles.
   */
  private remeshTouched(trigger: number): void {
    const { rest, touched, adjOffsets, adjNeighbors } = this;
    const n = this.vertexCount;
    const high2 = (EDGE_LENGTH * 4 / 3) ** 2, low2 = (EDGE_LENGTH * 4 / 5) ** 2;
    const uneven = new Uint8Array(n), region = new Uint8Array(n);
    let any = false;
    for (let v = 0; v < n; v++) {
      if (!touched[v]) continue;
      for (let e = adjOffsets[v]; e < adjOffsets[v + 1]; e++) {
        const u = adjNeighbors[e];
        const dx = rest[u * 3] - rest[v * 3], dy = rest[u * 3 + 1] - rest[v * 3 + 1];
        const dz = rest[u * 3 + 2] - rest[v * 3 + 2];
        const d2 = dx * dx + dy * dy + dz * dz;
        if (d2 > high2 || d2 < low2) {
          uneven[v] = uneven[u] = 1;
          any = true;
        }
      }
    }
    if (!any) return;
    for (let v = 0; v < n; v++) {
      if (!uneven[v]) continue;
      region[v] = 1;
      for (let e = adjOffsets[v]; e < adjOffsets[v + 1]; e++) region[adjNeighbors[e]] = 1;
    }
    // A grab's hold, and the walls it started from, go with the clay.
    const s = this.stroke, grab = s?.tool === "grab" ? [...s.grabBeyond, s.stretchFrom] : [];
    const result = remesh(
      this.rest,
      this.index,
      [
        { data: this.display, stride: 3, follows: true },
        { data: this.velocity, stride: 3 },
        { data: this.weights, stride: 1 },
        { data: this.touched, stride: 1 },
        { data: this.thickness, stride: 1 },
        { data: this.gap, stride: 1 },
        ...grab.map((data) => ({ data, stride: 1 })),
      ],
      EDGE_LENGTH,
      { region, trigger },
    );
    if (!result) return;
    const [display, velocity, weights, touchedAfter, thickness, gap, ...grabAfter] =
      result.channels;
    this.setMesh(
      result.positions,
      result.index,
      display,
      velocity,
      weights,
      touchedAfter,
      thickness,
      gap,
    );
    if (s?.tool === "grab") {
      s.stretchFrom = grabAfter.pop()!;
      s.grabBeyond = grabAfter;
      this.holdGrab(s);
    }
    this.version++;
    this.awake = true;
  }

  get isStroking(): boolean {
    return this.stroke !== null;
  }

  /**
   * Where brush cursors should be drawn during a stroke: with the mirror image, if any (the
   * wheel's ring is shown apart).
   */
  get strokeCursors(): Hit[] {
    const s = this.stroke;
    if (!s || s.hole) return [];
    const hit = { point: s.point, normal: s.normal };
    return s.repeat === "wheel" ? [hit] : this.dabs(hit);
  }

  /** Whether the stroke works its mirror image too (on a hole, only where there is one). */
  get mirrored(): boolean {
    const repeat = this.stroke?.repeat;
    return repeat !== undefined && repeat !== null && repeat !== "wheel";
  }

  reset(): void {
    this.restore({ rest: this.initialMesh.positions, index: this.initialMesh.index });
  }

  /** The kneaded shape with its current mesh, for undo. */
  snapshot(): ClaySnapshot {
    return { rest: this.rest.slice(), index: this.index.slice() };
  }

  restore({ rest, index }: ClaySnapshot): void {
    this.endStroke();
    this.setMesh(rest.slice(), index.slice());
    this.version++;
    this.awake = true;
  }

  /** Front-face ray cast against the visible surface (Möller–Trumbore, no allocations per triangle). */
  raycast(ray: THREE.Ray): Hit | null {
    const sphere = this.mesh.geometry.boundingSphere;
    if (sphere && !ray.intersectsSphere(sphere)) return null;

    const pos = this.display, index = this.index;
    const ox = ray.origin.x, oy = ray.origin.y, oz = ray.origin.z;
    const dx = ray.direction.x, dy = ray.direction.y, dz = ray.direction.z;
    let bestT = Infinity;
    let bestTri = -1;
    for (let t = 0; t < index.length; t += 3) {
      const ia = index[t] * 3, ib = index[t + 1] * 3, ic = index[t + 2] * 3;
      const ax = pos[ia], ay = pos[ia + 1], az = pos[ia + 2];
      const e1x = pos[ib] - ax, e1y = pos[ib + 1] - ay, e1z = pos[ib + 2] - az;
      const e2x = pos[ic] - ax, e2y = pos[ic + 1] - ay, e2z = pos[ic + 2] - az;
      const px = dy * e2z - dz * e2y, py = dz * e2x - dx * e2z, pz = dx * e2y - dy * e2x;
      const det = e1x * px + e1y * py + e1z * pz;
      if (det <= 1e-12) continue; // back face or parallel
      const inv = 1 / det;
      const sx = ox - ax, sy = oy - ay, sz = oz - az;
      const u = (sx * px + sy * py + sz * pz) * inv;
      if (u < 0 || u > 1) continue;
      const qx = sy * e1z - sz * e1y, qy = sz * e1x - sx * e1z, qz = sx * e1y - sy * e1x;
      const v = (dx * qx + dy * qy + dz * qz) * inv;
      if (v < 0 || u + v > 1) continue;
      const dist = (e2x * qx + e2y * qy + e2z * qz) * inv;
      if (dist > 1e-6 && dist < bestT) {
        bestT = dist;
        bestTri = t;
      }
    }
    if (bestTri < 0) return null;

    const ia = index[bestTri] * 3, ib = index[bestTri + 1] * 3, ic = index[bestTri + 2] * 3;
    const a = new THREE.Vector3().fromArray(pos, ia);
    const e1 = new THREE.Vector3().fromArray(pos, ib).sub(a);
    const e2 = new THREE.Vector3().fromArray(pos, ic).sub(a);
    return {
      point: ray.at(bestT, new THREE.Vector3()),
      normal: e1.cross(e2).normalize(),
    };
  }

  /**
   * The hole `ray` looks through, if it does: the ray, a line from the camera, touches no
   * clay, and some loop round the surface winds round it (see windsRound). Parts that only
   * look closed round the line from where it is seen (the ends of a bent piece crossing in
   * front of each other) are no hole. The hole's rim is then the vertex nearest the line
   * each way round it: the hole's outline as seen along the line.
   */
  holeAt(ray: THREE.Ray): Hole | null {
    const { rest } = this, n = this.vertexCount;
    // A closed surface with g holes has V - E + F = 2 - 2g, where E = 3F/2.
    const holes = 1 - (n - this.index.length / 6) / 2;
    if (holes < 1) return null;
    const sphere = this.mesh.geometry.boundingSphere;
    if (sphere && !ray.intersectsSphere(sphere)) return null;
    const { origin: o, direction: d } = ray;
    if (this.rayCrossings(o.x, o.y, o.z, d.x, d.y, d.z).length > 0) return null;
    const [u, v] = acrossLine(d);
    const angle = new Float64Array(n);
    const outline = new Float32Array(HOLE_SECTORS).fill(Infinity);
    const nearest = new Int32Array(HOLE_SECTORS).fill(-1);
    for (let i = 0; i < n; i++) {
      const x = rest[i * 3] - o.x, y = rest[i * 3 + 1] - o.y, z = rest[i * 3 + 2] - o.z;
      const a = x * u.x + y * u.y + z * u.z, b = x * v.x + y * v.y + z * v.z;
      angle[i] = Math.atan2(b, a);
      const far = Math.hypot(a, b), k = sectorOf(angle[i]);
      if (far < outline[k]) {
        outline[k] = far;
        nearest[k] = i;
      }
    }
    if (!this.windsRound(angle)) return null;
    const rim = Uint32Array.from(new Set(nearest.filter((i) => i >= 0)));
    let along = 0;
    for (const i of rim) {
      along += (rest[i * 3] - o.x) * d.x + (rest[i * 3 + 1] - o.y) * d.y +
        (rest[i * 3 + 2] - o.z) * d.z;
    }
    const point = ray.at(along / rim.length, new THREE.Vector3());
    // The outline's corners, one a sector, and the centroid of the fan they make round the
    // line (on it, however far off its middle the line runs).
    const corners: [number, number][] = [];
    for (let k = 0; k < HOLE_SECTORS; k++) {
      const a = (k + 0.5) / HOLE_SECTORS * 2 * Math.PI - Math.PI;
      corners.push([outline[k] * Math.cos(a), outline[k] * Math.sin(a)]);
    }
    let area = 0, mx = 0, my = 0;
    corners.forEach(([ax, ay], k) => {
      const [bx, by] = corners[(k + 1) % HOLE_SECTORS];
      const t = (ax * by - ay * bx) / 2;
      if (!Number.isFinite(t)) return;
      area += t;
      mx += t * (ax + bx) / 3;
      my += t * (ay + by) / 3;
    });
    mx /= area;
    my /= area;
    let size = 0, sides = 0;
    for (const [x, y] of corners) {
      if (!Number.isFinite(x)) continue;
      size += Math.hypot(x - mx, y - my);
      sides++;
    }
    const middle = point.clone().addScaledVector(u, mx).addScaledVector(v, my);
    return { line: ray.clone(), point, middle, size: size / sides, rim, outline };
  }

  /**
   * Whether some loop round the surface winds round the line the vertices' `angle`s are
   * measured round (a line touching no clay). The angle is carried from vertex to vertex
   * over the surface, each edge turning it less than half round; an edge closing a loop
   * with the angle carried there a whole turn off closes a loop that winds round the line.
   * With no hole round the line, every loop bounds a patch of surface the line does not go
   * through, and comes back to the angle it started from.
   */
  private windsRound(angle: Float64Array): boolean {
    const { adjOffsets, adjNeighbors } = this;
    const n = this.vertexCount;
    const carried = new Float64Array(n), reached = new Uint8Array(n), queue = new Uint32Array(n);
    reached[0] = 1;
    carried[0] = angle[0];
    let count = 1;
    for (let k = 0; k < count; k++) {
      const i = queue[k];
      for (let e = adjOffsets[i]; e < adjOffsets[i + 1]; e++) {
        const j = adjNeighbors[e];
        let turn = angle[j] - angle[i];
        if (turn > Math.PI) turn -= 2 * Math.PI;
        else if (turn < -Math.PI) turn += 2 * Math.PI;
        if (!reached[j]) {
          reached[j] = 1;
          carried[j] = carried[i] + turn;
          queue[count++] = j;
        } else if (Math.abs(carried[i] + turn - carried[j]) > Math.PI) return true;
      }
    }
    return false;
  }

  /**
   * Starts a stroke on the clay at a hit or on a hole looked through, repeated as `repeat`
   * says: on a hole, mirrored only if the clay has a hole at the mirror image too, and
   * never round the wheel.
   */
  beginStroke(tool: Tool, at: Hit | Hole, viewDir: THREE.Vector3, repeat: Repeat = null): void {
    this.endStroke();
    const holes = "rim" in at ? this.holeDabs(at, repeat) : [];
    if (holes.length === 1) repeat = null;
    const hit = "rim" in at ? { point: at.point, normal: new THREE.Vector3() } : at;
    const wheelGrab = tool === "grab" && repeat === "wheel";
    const s: Stroke = {
      tool,
      repeat,
      hole: holes.length > 0,
      copies: wheelGrab ? [new THREE.Matrix4()] : copiesOf(repeat, hit, this.brushRadius),
      side: repeat === "wheel" ? sideOf(hit.point) : null,
      plane: new THREE.Plane().setFromNormalAndCoplanarPoint(viewDir, hit.point),
      point: hit.point.clone(),
      normal: hit.normal.clone(),
      grabBeyond: [],
      stretchFrom: new Float32Array(0),
      grabReach: 0,
      pulled: new THREE.Vector3(),
    };
    this.stroke = s;
    this.measureSeparation(true);
    const dabs = this.dabs(hit);
    let centers = dabs.map((d) => d.point), core = 0;
    if (tool === "grab") {
      // Grab keeps hold of the same clay for the whole stroke; the other tools re-gather
      // every frame.
      const hold = s.hole ? this.holeHold(holes) : this.grabHold(dabs, wheelGrab);
      core = hold.core;
      centers = hold.centers;
      s.grabReach = hold.reach;
      s.grabBeyond = hold.beyond;
      s.stretchFrom = this.thickness.slice(0, this.vertexCount);
      this.holdGrab(s);
    }
    if (tool === "flatten") {
      s.plane.setFromNormalAndCoplanarPoint(viewDir.clone().negate(), hit.point);
    }
    if (tool !== "grab") {
      if (s.hole) this.useFootprint(this.merge(this.holeFootprints(tool, holes)));
      else this.gatherBrush(centers, falloff, core);
    }
    const kick = PRESS_IMPULSE * this.brushRadius * (tool === "smooth" ? 0.3 : 1);
    const { velocity, displayNormals, weights, active } = this;
    for (let k = 0; k < this.activeCount; k++) {
      const i = active[k], w = weights[i] * kick;
      velocity[i * 3] -= displayNormals[i * 3] * w;
      velocity[i * 3 + 1] -= displayNormals[i * 3 + 1] * w;
      velocity[i * 3 + 2] -= displayNormals[i * 3 + 2] * w;
    }
    this.awake = true;
  }

  /**
   * Advances the current stroke. Call every frame while the pointer is held.
   * `hit` is the current ray hit on the surface (needed by push/smooth); on a hole, the
   * tools other than grab work the hole the ray looks through.
   */
  dragStroke(ray: THREE.Ray, hit: Hit | null, dt: number): void {
    const s = this.stroke;
    if (!s) return;
    const r = this.brushRadius;

    if (s.tool === "grab") {
      const target = ray.intersectPlane(s.plane, _p);
      if (!target) return;
      const delta = _delta.subVectors(target, s.point);
      const len = delta.length();
      if (len < 1e-5) return;
      const maxStep = MAX_GRAB_STEP * r;
      if (len > maxStep) delta.multiplyScalar(maxStep / len);
      // Drawn out only as far as the clay allows: a thin part (a handle) has none to spare,
      // so pulling it longer would draw a thread. Easing back is always free.
      if (delta.dot(s.pulled) > 0) delta.multiplyScalar(this.stretchRoom(s.stretchFrom));
      if (s.hole) delta.multiplyScalar(this.holeRoom(delta));
      if (delta.lengthSq() < 1e-12) return;
      s.point.add(delta);
      const was = s.pulled.clone(), reachWas = this.grabReach(s);
      s.pulled.add(delta);
      const reach = this.grabReach(s);
      const pulls = s.copies.map((m) => s.pulled.clone().applyMatrix4(m));
      const pullsWas = s.copies.map((m) => was.clone().applyMatrix4(m));
      const { rest, restNormals: nr, roomIn, roomOut, weights, active } = this;
      for (let k = 0; k < this.activeCount; k++) {
        const i = active[k], o = i * 3;
        // Each vertex follows its weight times the whole pull, and the weight grows as the
        // hold spreads. Where dabs overlap (on the mirror plane, or between the wheel's) their
        // pulls are averaged: along the plane the clay moves once, across it the pulls cancel.
        let mx = 0, my = 0, mz = 0, wx = 0, wy = 0, wz = 0, sum = 0, sumWas = 0, held = 0;
        for (let d = 0; d < s.grabBeyond.length; d++) {
          const w = falloff(s.grabBeyond[d][i] / reach);
          const wWas = falloff(s.grabBeyond[d][i] / reachWas);
          mx += pulls[d].x * w;
          my += pulls[d].y * w;
          mz += pulls[d].z * w;
          wx += pullsWas[d].x * wWas;
          wy += pullsWas[d].y * wWas;
          wz += pullsWas[d].z * wWas;
          sum += w;
          sumWas += wWas;
          held = Math.max(held, w);
        }
        const now = 1 / Math.max(1, sum), before = 1 / Math.max(1, sumWas);
        mx = mx * now - wx * before;
        my = my * now - wy * before;
        mz = mz * now - wz * before;
        if (s.side) {
          const { c, s: sn } = turnTo(s.side, rest, o);
          const tx = c * mx + sn * mz;
          mz = -sn * mx + c * mz;
          mx = tx;
        }
        weights[i] = held;
        if (held > 0) this.touched[i] = 1;
        // Moving out against a narrow gap slows to a stop, so a pulled flap cannot be
        // pressed through the clay it folds onto; sliding along the surface stays free. A
        // hole's walls, held without the clay behind them, slow into a thin wall too.
        const along = mx * nr[o] + my * nr[o + 1] + mz * nr[o + 2];
        const kept = along > 0 ? along * (1 - roomOut[i]) : s.hole ? along * (1 - roomIn[i]) : 0;
        rest[o] += mx - nr[o] * kept;
        rest[o + 1] += my - nr[o + 1] * kept;
        rest[o + 2] += mz - nr[o + 2] * kept;
      }
      this.settle(ROUNDING, "body");
    } else if (s.hole) {
      if (!this.workHole(s, ray, dt)) return;
    } else {
      if (!hit) return;
      s.point.copy(hit.point);
      s.normal.copy(hit.normal);
      const broad = s.tool === "inflate" || s.tool === "flatten";
      const shape = broad ? plateauFalloff : falloff;
      // Inflate swells a part thin enough for the brush all around, from a ball in its
      // middle, so a thin handle gets thicker instead of growing a fin toward the camera. The
      // other tools work the side facing the brush.
      const core = s.tool === "inflate" ? this.inflateCore(hit) : 0;
      s.copies = copiesOf(s.repeat, hit, r);
      if (s.repeat === "wheel") s.side = sideOf(hit.point);
      // Inflate on the wheel works one even ring, its own normals the way out all round.
      const ring = s.side !== null && s.tool === "inflate";
      const dabs = (ring ? [hit] : this.dabs(hit)).map((dab, d) => {
        const plane = s.plane.clone().applyMatrix4(s.copies[d]);
        const { center, n } = this.gatherDab(s.tool, dab, core, plane, true, ring);
        return { center, n, plane, footprint: this.footprint() };
      });
      // Where dabs overlap (on the mirror plane, or between the wheel's) each counts its share,
      // so the clay there is not worked twice.
      const sum = new Float32Array(dabs.length > 1 ? this.vertexCount : 0);
      for (const { footprint: f } of dabs.length > 1 ? dabs : []) {
        for (let k = 0; k < f.index.length; k++) sum[f.index[k]] += f.weight[k];
      }
      if (s.side && !ring) {
        // The wheel's dabs make one ring, worked by the tool held at the pointer as the clay
        // turns under it.
        const ring: number[] = [];
        for (let i = 0; i < sum.length; i++) if (sum[i] > 0) ring.push(i);
        const index = Uint32Array.from(ring);
        this.useFootprint({ index, weight: Float32Array.from(index, (i) => Math.min(1, sum[i])) });
        this.applyDab(s.tool, dabs[0].n, dabs[0].plane, dt, s.side);
      } else {
        for (const { footprint: f } of dabs.length > 1 ? dabs : []) {
          for (let k = 0; k < f.index.length; k++) {
            const total = sum[f.index[k]];
            if (total > 1) f.weight[k] /= total;
          }
        }
        for (const dab of dabs) {
          this.useFootprint(dab.footprint);
          this.applyDab(s.tool, dab.n, dab.plane, dt, null);
        }
      }
      // Volume and mesh upkeep see the whole brush footprint.
      if (dabs.length > 1) this.gatherBrush(dabs.map((d) => d.center), shape, core);
      if (s.tool === "push") {
        // Displaced clay bulges up around the dent.
        this.settle(0, dabs.map((d) => d.center));
      } else {
        this.settle(s.tool === "inflate" ? ROUNDING : 0, s.tool === "smooth" ? "brush" : "body");
      }
    }
    this.version++;
    this.awake = true;
    if (++this.stepsSinceRemesh >= REMESH_INTERVAL) {
      this.stepsSinceRemesh = 0;
      this.remeshTouched(STROKE_REMESH_TRIGGER);
    }
  }

  /** The stroke's hit, and its copies (see copiesOf). */
  private dabs(hit: Hit): Hit[] {
    return dabsOf(hit, this.stroke?.copies ?? []);
  }

  /**
   * One dab of push / inflate / flatten / smooth with the brush weights just gathered.
   * Pressing slows to a stop on thin walls, swelling on narrow gaps. On the wheel (`side`),
   * push and flatten turn round with the clay (see Stroke.side).
   */
  private applyDab(
    tool: Tool,
    n: THREE.Vector3,
    plane: THREE.Plane,
    dt: number,
    side: Side | null,
  ): void {
    const { rest, restNormals: nr, weights, active } = this;
    const r = this.brushRadius;
    if (tool === "push") {
      // A thumb press: the whole spot sinks along its mean normal.
      const depth = PUSH_SPEED * r * dt;
      for (let k = 0; k < this.activeCount; k++) {
        const i = active[k], w = weights[i] * depth * this.roomIn[i];
        let nx = n.x, nz = n.z;
        if (side) {
          const { c, s } = turnTo(side, rest, i * 3);
          nx = c * n.x + s * n.z;
          nz = -s * n.x + c * n.z;
        }
        rest[i * 3] -= nx * w;
        rest[i * 3 + 1] -= n.y * w;
        rest[i * 3 + 2] -= nz * w;
      }
    } else if (tool === "inflate") {
      // Each vertex moves along its own normal, so the spot swells into a round mound.
      const height = PUSH_SPEED * r * dt;
      for (let k = 0; k < this.activeCount; k++) {
        const i = active[k], w = weights[i] * height * this.roomOut[i];
        rest[i * 3] += nr[i * 3] * w;
        rest[i * 3 + 1] += nr[i * 3 + 1] * w;
        rest[i * 3 + 2] += nr[i * 3 + 2] * w;
      }
    } else if (tool === "flatten") {
      // Bumps are pressed down and dips filled up to one plane: flat faces, crisp edges.
      const pull = Math.min(1, FLATTEN_RATE * dt);
      const { normal: pn, constant } = plane;
      for (let k = 0; k < this.activeCount; k++) {
        const i = active[k], o = i * 3;
        // On the wheel the plane turns about the axis (keeping its distance from it).
        let px = pn.x, pz = pn.z;
        if (side) {
          const { c, s } = turnTo(side, rest, o);
          px = c * pn.x + s * pn.z;
          pz = -s * pn.x + c * pn.z;
        }
        const d = rest[o] * px + rest[o + 1] * pn.y + rest[o + 2] * pz + constant;
        // Only clay near the plane is flattened: whatever lies farther off than the brush (a
        // handle standing out in front of the face begun on, the far side of a ring seen
        // through it) is left alone, not pressed back through itself or drawn across a gap.
        const near = 1 - smoothstep(r, 2 * r, Math.abs(d));
        const m = THREE.MathUtils.clamp(d * pull * weights[i] * near, -FLATTEN_STEP, FLATTEN_STEP);
        const mx = -px * m, my = -pn.y * m, mz = -pz * m;
        // Pressing into a thin wall, or out against a narrow gap, slows to a stop as pushing
        // and swelling do: a plane set further back (the stroke begun on clay behind) would
        // otherwise press a handle's near side through its far one. Sliding along the
        // surface stays free.
        const along = mx * nr[o] + my * nr[o + 1] + mz * nr[o + 2];
        const held = along * (1 - (along < 0 ? this.roomIn[i] : this.roomOut[i]));
        rest[o] += mx - nr[o] * held;
        rest[o + 1] += my - nr[o + 1] * held;
        rest[o + 2] += mz - nr[o + 2] * held;
      }
    } else if (tool === "smooth") {
      const passes = Math.round(SMOOTH_PASSES_PER_SECOND * dt);
      for (let pass = 0; pass < passes; pass++) this.smooth(0.5);
    }
  }

  /**
   * The holes a stroke on `hole` works: the hole, and in symmetry mode the one its mirror
   * image looks through, if the clay has a hole there. The wheel does not work holes.
   */
  private holeDabs(hole: Hole, repeat: Repeat): Hole[] {
    if (repeat === null || repeat === "wheel") return [hole];
    const mirrored = this.holeAt(mirrorRay(hole.line, repeat.mirror));
    return mirrored ? [hole, mirrored] : [hole];
  }

  /**
   * How far each vertex sits back from the hole's outline (see Hole.outline), square to the
   * line through it, over the surface reached from the rim without going `limit` back or
   * further; FAR elsewhere. The walls of a straight hole all sit on its outline, those of a
   * round one further back the nearer its mouths.
   */
  private beyondRim(hole: Hole, limit: number): Float32Array {
    const { rest, adjOffsets, adjNeighbors, flagged } = this;
    const n = this.vertexCount, { origin, direction: d } = hole.line, [u, v] = acrossLine(d);
    const back = new Float32Array(n);
    for (let i = 0; i < n; i++) {
      const far = awayFromLine(origin, d, rest, i * 3, _out);
      const to = outlineAt(hole.outline, Math.atan2(_out.dot(v), _out.dot(u)));
      back[i] = Number.isFinite(to) ? Math.max(0, far - to) : FAR;
    }
    const beyond = new Float32Array(n).fill(FAR);
    let count = 0;
    for (const i of hole.rim) {
      beyond[i] = back[i];
      flagged[count++] = i;
    }
    for (let k = 0; k < count; k++) {
      const i = flagged[k];
      for (let e = adjOffsets[i]; e < adjOffsets[i + 1]; e++) {
        const j = adjNeighbors[e];
        if (beyond[j] !== FAR || back[j] >= limit) continue;
        beyond[j] = back[j];
        flagged[count++] = j;
      }
    }
    return beyond;
  }

  /**
   * What a grab on `holes` takes hold of: each hole's rim, the hold weakening away from it as
   * a grab's does beyond its core (see grabHold, beyondRim).
   */
  private holeHold(holes: Hole[]): {
    core: number;
    centers: THREE.Vector3[];
    reach: number;
    beyond: Float32Array[];
  } {
    const r = this.brushRadius;
    return {
      core: 0,
      centers: holes.map((h) => h.point),
      reach: r,
      beyond: holes.map((h) => this.beyondRim(h, GRAB_MAX_REACH * r)),
    };
  }

  /**
   * Gathers the brush on a hole: the surface round its rim set back from its outline by
   * less than the brush radius, the less the stronger (see beyondRim), so a small brush
   * works the narrowest of a round hole alone and a big one its walls out to its mouths,
   * and a straight hole all along. The tools that move the clay to or from the line
   * through the hole keep to the walls turned toward it, not the faces round its mouths;
   * smoothing evens out the lot. `mark` notes the vertices for remeshing.
   */
  private gatherHole(tool: Tool, hole: Hole, mark: boolean): void {
    const { rest, restNormals: nr, weights, active } = this;
    const r = this.brushRadius, dist = this.beyondRim(hole, r);
    const { origin, direction: d } = hole.line;
    for (let k = 0; k < this.activeCount; k++) weights[active[k]] = 0;
    let count = 0;
    for (let i = 0; i < this.vertexCount; i++) {
      if (dist[i] >= r) continue;
      const o = i * 3;
      let w = falloff(dist[i] / r);
      if (tool !== "smooth") {
        awayFromLine(origin, d, rest, o, _out);
        const toward = -(nr[o] * _out.x + nr[o + 1] * _out.y + nr[o + 2] * _out.z);
        w *= smoothstep(0, FACING_SPREAD, toward);
      }
      if (w <= 0) continue;
      weights[i] = w;
      if (mark) this.touched[i] = 1;
      active[count++] = i;
    }
    this.activeCount = count;
  }

  /**
   * The brush on each of `holes` (see gatherHole). Where they overlap (a hole the mirror
   * plane runs through, worked from both sides) each counts its share, so the clay there
   * is not worked twice.
   */
  private holeFootprints(tool: Tool, holes: Hole[], mark = true): Footprint[] {
    const footprints = holes.map((h) => {
      this.gatherHole(tool, h, mark);
      return this.footprint();
    });
    if (footprints.length < 2) return footprints;
    const sum = new Float32Array(this.vertexCount);
    for (const f of footprints) {
      for (let k = 0; k < f.index.length; k++) sum[f.index[k]] += f.weight[k];
    }
    for (const f of footprints) {
      for (let k = 0; k < f.index.length; k++) {
        const total = sum[f.index[k]];
        if (total > 1) f.weight[k] /= total;
      }
    }
    return footprints;
  }

  /** Footprints (their shares, see holeFootprints) added into one. */
  private merge(footprints: Footprint[]): Footprint {
    if (footprints.length === 1) return footprints[0];
    const sum = new Float32Array(this.vertexCount);
    for (const f of footprints) {
      for (let k = 0; k < f.index.length; k++) sum[f.index[k]] += f.weight[k];
    }
    const index = Uint32Array.from(sum.keys()).filter((i) => sum[i] > 0);
    return { index, weight: Float32Array.from(index, (i) => sum[i]) };
  }

  /**
   * A step of push / inflate / flatten / smooth on the hole `ray` looks through, if it
   * still does (and on the hole at its mirror image, see holeDabs). Clay pushed back is
   * given back around the rim, clay inflated taken from the whole piece, as on the clay.
   * Returns whether there was a hole to work.
   */
  private workHole(s: Stroke, ray: THREE.Ray, dt: number): boolean {
    const hole = this.holeAt(ray);
    if (!hole) return false;
    const holes = this.holeDabs(hole, s.repeat);
    const footprints = this.holeFootprints(s.tool, holes);
    footprints.forEach((f, d) => {
      this.useFootprint(f);
      this.applyHoleDab(s.tool, holes[d], dt);
    });
    // Volume and mesh upkeep see the whole brush footprint, the faces round the hole's
    // mouths as well: rounded off with the walls, they do not crease where the walls move.
    this.useFootprint(this.merge(this.holeFootprints("smooth", holes)));
    if (s.tool === "push") {
      const rims = holes.flatMap((h) =>
        Array.from(
          h.rim.filter((_, k) => k % RIM_REFILL_EVERY === 0),
          (i) => new THREE.Vector3().fromArray(this.rest, i * 3),
        )
      );
      this.settle(0, rims);
    } else {
      this.settle(s.tool === "inflate" ? ROUNDING : 0, s.tool === "smooth" ? "brush" : "body");
    }
    return true;
  }

  /**
   * One dab of push / inflate / flatten / smooth on a hole, with the weights just gathered
   * (see gatherHole). Push moves the walls out from the hole's middle, widening it. Inflate
   * moves them in toward it, narrowing it (to MIN_GAP across at the least), each wall by as
   * much of its distance from the middle, so the hole shrinks as it is, a round one staying
   * round, instead of its nearer sides closing in first into corners. Flatten draws the
   * walls out onto the hole's outline as seen along the line through it, so they run
   * straight along the line, a round hole turning into a cylinder. Moving into a thin wall
   * or out against a narrow gap slows to a stop, as flatten's does on the clay; sliding
   * along the surface stays free.
   */
  private applyHoleDab(tool: Tool, hole: Hole, dt: number): void {
    if (tool === "smooth") {
      const passes = Math.round(SMOOTH_PASSES_PER_SECOND * dt);
      for (let pass = 0; pass < passes; pass++) this.smooth(0.5);
      return;
    }
    const { rest, restNormals: nr, weights, active, roomIn, roomOut } = this;
    const r = this.brushRadius, { origin, direction: d } = hole.line, [u, v] = acrossLine(d);
    const pull = Math.min(1, FLATTEN_RATE * dt);
    // Push and inflate: how far the rim moves out. Once it can go no further (the hole as
    // narrow as it gets, or the clay round it as thin), the walls behind it stop too,
    // instead of swelling over its mouths into lips.
    let depth = 0;
    if (tool !== "flatten") {
      const room = tool === "push" ? roomIn : roomOut;
      let rimRoom = 0;
      for (const i of hole.rim) rimRoom += room[i];
      depth = (tool === "push" ? 1 : -1) * PUSH_SPEED * r * dt * rimRoom / hole.rim.length;
    }
    for (let k = 0; k < this.activeCount; k++) {
      const i = active[k], o = i * 3;
      let m: number;
      if (tool === "flatten") {
        const far = awayFromLine(origin, d, rest, o, _out);
        const to = outlineAt(hole.outline, Math.atan2(_out.dot(v), _out.dot(u)));
        if (!Number.isFinite(to)) continue;
        // Only the walls near the outline are drawn in, as flatten keeps near its plane.
        const off = far - to, near = 1 - smoothstep(r, 2 * r, Math.abs(off));
        m = THREE.MathUtils.clamp(-off * pull * weights[i] * near, -FLATTEN_STEP, FLATTEN_STEP);
      } else {
        const far = awayFromLine(hole.middle, d, rest, o, _out);
        // Narrowing, each wall moves in by its distance from the middle over the hole's size
        // (the rim by `depth`), or over the brush radius for a hole smaller than the brush,
        // so a small hole is not shut at a touch.
        m = depth * weights[i] *
          (tool === "push" ? 1 : Math.min(far, 2 * hole.size) / Math.max(hole.size, r));
      }
      const mx = _out.x * m, my = _out.y * m, mz = _out.z * m;
      const along = mx * nr[o] + my * nr[o + 1] + mz * nr[o + 2];
      const held = along * (1 - (along < 0 ? roomIn[i] : roomOut[i]));
      rest[o] += mx - nr[o] * held;
      rest[o + 1] += my - nr[o + 1] * held;
      rest[o + 2] += mz - nr[o + 2] * held;
    }
  }

  endStroke(): void {
    const s = this.stroke;
    if (!s) return;
    this.stroke = null;
    for (let k = 0; k < this.activeCount; k++) this.weights[this.active[k]] = 0;
    this.activeCount = 0;
    // A grab let go, the walls it thinned are mended. The stroke's last step measured them;
    // after that only thin ones are measured again, as nothing is held any more.
    if (s.tool === "grab") {
      for (let step = 0; step < RELEASE_STEPS; step++) {
        if (step > 0) this.measureSeparation(false);
        this.separate();
        this.preserveVolume("body");
      }
    }
    this.stepsSinceRemesh = 0;
    this.remeshTouched(4 / 3);
    this.touched.fill(0);
    this.recenter();
  }

  /** Steps the display spring. Returns true if the visible surface moved. */
  update(dt: number): boolean {
    if (!this.awake) return false;
    const steps = Math.max(1, Math.ceil(dt * 120));
    const h = dt / steps;
    const { display, rest, velocity } = this;
    let maxErr = 0, maxVel = 0;
    for (let s = 0; s < steps; s++) {
      const last = s === steps - 1;
      for (let i = 0; i < display.length; i++) {
        const v = velocity[i] + (SPRING_K * (rest[i] - display[i]) - SPRING_C * velocity[i]) * h;
        velocity[i] = v;
        display[i] += v * h;
        if (last) {
          maxErr = Math.max(maxErr, Math.abs(rest[i] - display[i]));
          maxVel = Math.max(maxVel, Math.abs(v));
        }
      }
    }
    if (!this.stroke && maxErr < 1e-4 && maxVel < 1e-3) {
      display.set(rest);
      velocity.fill(0);
      this.awake = false;
    }
    computeStats(display, this.index, this.displayNormals, this.displayAreas);
    const geometry = this.mesh.geometry;
    geometry.getAttribute("position").needsUpdate = true;
    geometry.getAttribute("normal").needsUpdate = true;
    geometry.computeBoundingSphere();
    return true;
  }

  /**
   * How far the clay goes straight in from a surface hit: the distance between the first
   * two surface crossings of a ray coming in along the hit normal. Starting outside keeps
   * a slightly lagging visible surface from being mistaken for the far side.
   */
  /**
   * How deep inflate's ball sits under `hit`: in the middle of a part up to twice the brush
   * radius thick (the brush holds its whole cross-section), rising to the surface of parts
   * three times as thick or more.
   */
  private inflateCore(hit: Hit): number {
    const r = this.brushRadius, depth = this.depthBelow(hit);
    if (!Number.isFinite(depth)) return 0;
    return (depth / 2) * Math.min(1, Math.max(0, (3 * r - depth) / r));
  }

  private depthBelow(hit: Hit): number {
    const { point: o, normal: n } = hit;
    const start = 0.1;
    const t = this.rayCrossings(
      o.x + n.x * start,
      o.y + n.y * start,
      o.z + n.z * start,
      -n.x,
      -n.y,
      -n.z,
    ).sort((a, b) => a - b);
    return t.length >= 2 ? t[1] - t[0] : Infinity;
  }

  /**
   * The clay a grab at `dabs` takes hold of: a ball (a ring round the axis on the wheel) as
   * deep as the part allows under each, with how far each vertex lies beyond it, and the
   * falloff distance before any pull.
   */
  private grabHold(dabs: Hit[], wheel: boolean): {
    core: number;
    centers: THREE.Vector3[];
    reach: number;
    beyond: Float32Array[];
  } {
    const r = this.brushRadius, { rest } = this;
    const core = Math.min(GRAB_DEPTH * r, ...dabs.map((d) => this.depthBelow(d) / 2));
    const centers = dabs.map((d) => d.point.clone().addScaledVector(d.normal, -core));
    const beyond = centers.map((c) => {
      const b = new Float32Array(this.vertexCount);
      // On the wheel, the core turns with the clay: a ring round the axis.
      const ring = wheel ? Math.hypot(c.x, c.z) : 0;
      for (let i = 0; i < this.vertexCount; i++) {
        const x = rest[i * 3], y = rest[i * 3 + 1], z = rest[i * 3 + 2];
        const d = wheel
          ? Math.hypot(Math.hypot(x, z) - ring, y - c.y)
          : Math.hypot(x - c.x, y - c.y, z - c.z);
        b[i] = Math.max(0, d - core);
      }
      return b;
    });
    return { core, centers, reach: Math.sqrt(r * r + core * core) - core, beyond };
  }

  /**
   * Gathers one dab's brush as `tool` works it (inflate around a ball `core` deep under
   * it), kept to the surface it touches: flatten to the side facing its `plane`, push and
   * smooth to the side facing the brush. Returns the dab's centre and the normal the tool
   * works along. `mark` notes the vertices for remeshing (not for the tint). On the `wheel`
   * the dab is a ring round the axis (see gatherBrush).
   */
  private gatherDab(
    tool: Tool,
    dab: Hit,
    core: number,
    plane: THREE.Plane,
    mark = true,
    wheel = false,
  ): { center: THREE.Vector3; n: THREE.Vector3 } {
    const shape = tool === "inflate" || tool === "flatten" ? plateauFalloff : falloff;
    const center = dab.point.clone().addScaledVector(dab.normal, -core);
    this.gatherBrush([center], shape, core, mark, wheel);
    let n = dab.normal;
    if (tool === "inflate") this.keepConnected(dab.point);
    else n = this.filterByNormal(dab.point, tool === "flatten" ? plane.normal : undefined);
    return { center, n };
  }

  /**
   * Tints the clay where a stroke of `tool` begun at `at` (a hit, or a hole looked through)
   * would work, the deeper the stronger, worked out as the stroke would gather it but
   * without working the clay; or, while a stroke goes on, where it is working. With
   * neither, the tint goes.
   */
  showBrush(
    tool: Tool,
    at: Hit | Hole | null,
    viewDir: THREE.Vector3,
    repeat: Repeat,
    color: THREE.Color,
  ): void {
    const s = this.stroke, n = this.vertexCount;
    const key = s ? "stroke" : at
      ? [
        tool,
        JSON.stringify(repeat),
        "rim" in at ? [...at.line.origin.toArray(), ...at.line.direction.toArray()] : "",
        at.point.toArray(),
        this.brushRadius,
        this.version,
        color.getHex(),
      ].join()
      : "";
    if (key === this.tinted && key !== "stroke") return;
    this.tinted = key;
    const shown = new Float32Array(n);
    if (s) {
      for (let k = 0; k < this.activeCount; k++) {
        shown[this.active[k]] = this.weights[this.active[k]];
      }
    } else if (at) this.brushWeights(tool, at, viewDir, repeat, shown);
    const attribute = this.tint.geometry.getAttribute("color") as THREE.BufferAttribute;
    const rgba = attribute.array as Float32Array;
    let any = false;
    for (let i = 0; i < n; i++) {
      const a = Math.min(1, shown[i]) * TINT_ALPHA;
      rgba[i * 4] = color.r;
      rgba[i * 4 + 1] = color.g;
      rgba[i * 4 + 2] = color.b;
      rgba[i * 4 + 3] = a;
      if (a > 0) any = true;
    }
    attribute.needsUpdate = true;
    this.tint.visible = any;
  }

  /** The weights a stroke of `tool` begun at `at` would start with, into `out`. */
  private brushWeights(
    tool: Tool,
    at: Hit | Hole,
    viewDir: THREE.Vector3,
    repeat: Repeat,
    out: Float32Array,
  ): void {
    const r = this.brushRadius;
    const holes = "rim" in at ? this.holeDabs(at, repeat) : [];
    if (tool === "grab") {
      const wheel = repeat === "wheel";
      const { reach, beyond } = "rim" in at
        ? this.holeHold(holes)
        : this.grabHold(dabsOf(at, wheel ? [new THREE.Matrix4()] : copiesOf(repeat, at, r)), wheel);
      for (const b of beyond) {
        for (let i = 0; i < out.length; i++) out[i] = Math.max(out[i], falloff(b[i] / reach));
      }
      return;
    }
    const { weights, active } = this;
    if ("rim" in at) {
      for (const f of this.holeFootprints(tool, holes, false)) {
        for (let k = 0; k < f.index.length; k++) out[f.index[k]] += f.weight[k];
      }
    } else {
      const core = tool === "inflate" ? this.inflateCore(at) : 0;
      const ring = repeat === "wheel" && tool === "inflate" && sideOf(at.point) !== null;
      const copies = ring ? [new THREE.Matrix4()] : copiesOf(repeat, at, r);
      const plane = new THREE.Plane().setFromNormalAndCoplanarPoint(
        viewDir.clone().negate(),
        at.point,
      );
      dabsOf(at, copies).forEach((dab, d) => {
        this.gatherDab(tool, dab, core, plane.clone().applyMatrix4(copies[d]), false, ring);
        for (let k = 0; k < this.activeCount; k++) out[active[k]] += weights[active[k]];
      });
    }
    // Nothing is being worked: the brush's own weights go back to none.
    for (let k = 0; k < this.activeCount; k++) weights[active[k]] = 0;
    this.activeCount = 0;
  }

  /**
   * How much further a grab may pull: 1 while the clay it stretches (what it holds only in
   * part, between the held core and the clay left in place) is no thinner than it may get
   * freely, down to 0 where it may get no thinner (see STRETCH_SLACK). `from` is how thick
   * each vertex's wall was when the grab took hold. Each vertex's room is averaged over a
   * couple of rings first, as the room to move is (see updateRoom): walls are measured
   * vertex by vertex, and one measured too thin (its far side found beside the normal, on a
   * folded surface) would otherwise hold the whole grab still, while clay really drawn thin
   * is thin all round.
   */
  private stretchRoom(from: Float32Array): number {
    const { weights, active, thickness, adjOffsets, adjNeighbors, scratch } = this;
    const n = this.vertexCount, count = this.activeCount;
    const stretched = (i: number) => weights[i] > 0.02 && weights[i] < 0.98;
    scratch.fill(1, 0, n);
    let any = false;
    for (let k = 0; k < count; k++) {
      const i = active[k], t = thickness[i];
      // Negative: crossed into another part, not a wall (see measureSeparation).
      if (!stretched(i) || t < 0) continue;
      const free = Math.min(SEPARATE_BELOW * MIN_THICKNESS, from[i]);
      if (t >= free) continue;
      const stop = Math.max(free - STRETCH_SLACK * MIN_THICKNESS, STRETCH_FLOOR * MIN_THICKNESS);
      scratch[i] = stop < free ? smoothstep(stop, free, t) : 0;
      any = true;
    }
    if (!any) return 1;
    for (let pass = 0; pass < ROOM_SMOOTH_PASSES; pass++) {
      for (let k = 0; k < count; k++) {
        const i = active[k];
        let sum = scratch[i];
        for (let e = adjOffsets[i]; e < adjOffsets[i + 1]; e++) sum += scratch[adjNeighbors[e]];
        scratch[n + i] = sum / (adjOffsets[i + 1] - adjOffsets[i] + 1);
      }
      for (let k = 0; k < count; k++) scratch[active[k]] = scratch[n + active[k]];
    }
    let room = 1;
    for (let k = 0; k < count; k++) {
      const i = active[k];
      if (stretched(i)) room = Math.min(room, scratch[i]);
    }
    return room;
  }

  /**
   * How far a grab on a hole may move it `delta` further: 1 while the walls it moves into
   * the clay (those it holds well, turned against the move) have room, down to 0 where the
   * clay behind them is as thin as it may get. The hole comes up against the edge of the
   * clay round it and stops there, instead of pressing into it unevenly and crumpling it.
   */
  private holeRoom(delta: THREE.Vector3): number {
    const { restNormals: nr, weights, active, roomIn } = this;
    const len = delta.length();
    let room = 1;
    for (let k = 0; k < this.activeCount; k++) {
      const i = active[k], o = i * 3;
      if (weights[i] < 0.5) continue;
      const against = -(nr[o] * delta.x + nr[o + 1] * delta.y + nr[o + 2] * delta.z) / len;
      if (against > 0.5) room = Math.min(room, roomIn[i]);
    }
    return room;
  }

  /** The grab's falloff distance for the pull so far (see GRAB_SPREAD). */
  private grabReach(s: Stroke): number {
    const r = this.brushRadius, into = Math.max(0, -s.pulled.dot(s.normal));
    return Math.max(
      s.grabReach,
      Math.min(GRAB_MAX_REACH * r, GRAB_SPREAD * s.pulled.length()),
      Math.min(GRAB_MAX_PUSH_REACH * r, GRAB_PUSH_SPREAD * into),
    );
  }

  /** The furthest the grab's falloff can spread. */
  private grabWidest(s: Stroke): number {
    return Math.max(s.grabReach, GRAB_MAX_PUSH_REACH * this.brushRadius);
  }

  /**
   * Makes every vertex the grab can reach as its hold spreads active, weighted for the
   * pull so far. Done when the grab starts and again after remeshing.
   */
  private holdGrab(s: Stroke): void {
    const { weights, active } = this;
    for (let k = 0; k < this.activeCount; k++) weights[active[k]] = 0;
    const widest = this.grabWidest(s), reach = this.grabReach(s);
    let count = 0;
    for (let i = 0; i < this.vertexCount; i++) {
      let near = Infinity;
      for (const beyond of s.grabBeyond) near = Math.min(near, beyond[i]);
      if (near >= widest) continue;
      weights[i] = falloff(near / reach);
      // Only what actually moves needs remeshing.
      if (weights[i] > 0) this.touched[i] = 1;
      active[count++] = i;
    }
    this.activeCount = count;
  }

  /** The brush weights just gathered, to install again later (see useFootprint). */
  private footprint(): Footprint {
    const index = this.active.slice(0, this.activeCount);
    return { index, weight: Float32Array.from(index, (i) => this.weights[i]) };
  }

  /** Installs a dab's brush weights, filling `active`. */
  private useFootprint({ index, weight }: Footprint): void {
    const { weights, active } = this;
    for (let k = 0; k < this.activeCount; k++) weights[active[k]] = 0;
    for (let k = 0; k < index.length; k++) weights[index[k]] = weight[k];
    active.set(index);
    this.activeCount = index.length;
  }

  /**
   * Brush weights around the centers (the strongest where they overlap); fills `active`.
   * Everything within `core` of a center gets full weight. On the `wheel`, each center is
   * turned all the way round the upright axis: a ring.
   */
  private gatherBrush(
    centers: THREE.Vector3[],
    shape = falloff,
    core = 0,
    mark = true,
    wheel = false,
  ): void {
    const { rest, weights, active } = this;
    for (let k = 0; k < this.activeCount; k++) weights[active[k]] = 0;
    // Outside the core the weight falls off over `reach`, sized so the footprint on a flat
    // surface touching the core stays the brush radius.
    const r = this.brushRadius, reach = Math.sqrt(r * r + core * core) - core;
    const outer2 = (core + reach) ** 2;
    let count = 0;
    for (let i = 0; i < this.vertexCount; i++) {
      let w = 0;
      for (const c of centers) {
        const dy = rest[i * 3 + 1] - c.y;
        let d2: number;
        if (wheel) {
          const dr = Math.hypot(rest[i * 3], rest[i * 3 + 2]) - Math.hypot(c.x, c.z);
          d2 = dr * dr + dy * dy;
        } else {
          const dx = rest[i * 3] - c.x, dz = rest[i * 3 + 2] - c.z;
          d2 = dx * dx + dy * dy + dz * dz;
        }
        if (d2 < outer2) w = Math.max(w, shape(Math.max(0, Math.sqrt(d2) - core) / reach));
      }
      if (w <= 0) continue;
      weights[i] = w;
      if (mark) this.touched[i] = 1;
      active[count++] = i;
    }
    this.activeCount = count;
  }

  /**
   * Keeps the brush to the surface around `point`: the vertices reached from it without
   * leaving the brush. Other parts the brush's ball reaches across a gap drop out. With
   * `facing`, the way only goes on through vertices turned toward it, so the far side of a
   * hole (reached around its rim) drops out too.
   */
  private keepConnected(point: THREE.Vector3, facing?: THREE.Vector3): void {
    const { rest, restNormals: nr, weights, active, adjOffsets, adjNeighbors, flags, flagged } =
      this;
    let start = -1, best = Infinity;
    for (let k = 0; k < this.activeCount; k++) {
      const i = active[k];
      const d = _p.fromArray(rest, i * 3).distanceToSquared(point);
      if (d < best) {
        best = d;
        start = i;
      }
    }
    if (start < 0) return;
    let count = 0;
    flags[start] = 1;
    flagged[count++] = start;
    for (let k = 0; k < count; k++) {
      const i = flagged[k], o = i * 3;
      if (
        facing && k > 0 &&
        nr[o] * facing.x + nr[o + 1] * facing.y + nr[o + 2] * facing.z < FACING_SPREAD
      ) continue;
      for (let e = adjOffsets[i]; e < adjOffsets[i + 1]; e++) {
        const j = adjNeighbors[e];
        if (flags[j] === 0 && weights[j] > 0) {
          flags[j] = 1;
          flagged[count++] = j;
        }
      }
    }
    let kept = 0;
    for (let k = 0; k < this.activeCount; k++) {
      const i = active[k];
      if (flags[i] === 1) active[kept++] = i;
      else weights[i] = 0;
    }
    this.activeCount = kept;
    for (let k = 0; k < count; k++) flags[flagged[k]] = 0;
  }

  /**
   * Fades out brush vertices facing away from `facing` (by default the weighted mean
   * normal of the brush, which is returned), so pressing one side of a thin part does not
   * drag the other side along; and keeps to the patch facing it around `point` (see
   * keepConnected), so a large brush flattening a ring from above leaves the lower rim of
   * its hole where it is instead of lifting it up into the hole.
   */
  private filterByNormal(point: THREE.Vector3, facing?: THREE.Vector3): THREE.Vector3 {
    const { restNormals: nr, weights, active } = this;
    const n = new THREE.Vector3();
    if (facing) n.copy(facing);
    else {
      for (let k = 0; k < this.activeCount; k++) {
        const i = active[k], w = weights[i];
        n.x += nr[i * 3] * w;
        n.y += nr[i * 3 + 1] * w;
        n.z += nr[i * 3 + 2] * w;
      }
      n.normalize();
    }
    for (let k = 0; k < this.activeCount; k++) {
      const i = active[k];
      const dot = nr[i * 3] * n.x + nr[i * 3 + 1] * n.y + nr[i * 3 + 2] * n.z;
      weights[i] *= smoothstep(-0.2, FACING_SPREAD, dot);
    }
    this.keepConnected(point, n);
    return n;
  }

  /** Uniform-Laplacian average of vertex i's one-ring, written to out[0..2]. */
  private ringAverage(i: number, out: Float32Array | number[]): void {
    const { rest, adjOffsets, adjNeighbors } = this;
    let x = 0, y = 0, z = 0;
    const start = adjOffsets[i], end = adjOffsets[i + 1];
    for (let e = start; e < end; e++) {
      const j = adjNeighbors[e] * 3;
      x += rest[j];
      y += rest[j + 1];
      z += rest[j + 2];
    }
    const inv = 1 / (end - start);
    out[0] = x * inv;
    out[1] = y * inv;
    out[2] = z * inv;
  }

  /** Mesh upkeep after every stroke step: relax, repair, keep sides apart, restore the volume. */
  private settle(rounding: number, refill: Refill): void {
    this.relax(rounding);
    this.repair();
    this.measureSeparation(false);
    this.separate();
    this.preserveVolume(refill);
  }

  /**
   * For each vertex, the nearest part of the surface facing it straight along its normal:
   * inward, the other side of the wall it is on (`thickness`); outward, across a gap
   * (`gap`). Unless `all`, only vertices that can have changed are measured: those under
   * the brush and the far sides of their walls and gaps, those thin or narrow enough to be
   * pushed apart (see separate), and a rotating share of the rest. Walls and gaps nearer
   * their minimum away from the brush hardly change between one measure and the next, and
   * measuring them every step made each step of a piece with thin walls all over slow.
   *
   * Surfaces can still pass through each other (a squeeze faster than the brakes), and up
   * close that looks like a wall or gap too:
   * - Two parts crossed into each other look like a thin wall, but the point between the
   *   two sides is inside both parts, so an even number of surfaces away from the outside.
   *   Recorded as a negative thickness; both sides retreat into their own parts.
   * - A wall pushed through itself looks like a narrow gap, but the clay between is inside
   *   out, an odd number of surfaces away from the outside. Recorded as a negative gap;
   *   both sides push back out.
   */
  private measureSeparation(all: boolean): void {
    const { rest, restNormals: nr, weights, thickness, gap, cellSlots, cellStart, cellItems } =
      this;
    const n = this.vertexCount, slots = cellStart.length - 1, mask = slots - 1;
    // Counting sort of the vertices into hashed grid cells.
    cellStart.fill(0);
    for (let i = 0; i < n; i++) {
      const s = cellSlot(rest[i * 3], rest[i * 3 + 1], rest[i * 3 + 2], mask);
      cellSlots[i] = s;
      cellStart[s]++;
    }
    for (let s = 1; s <= slots; s++) cellStart[s] += cellStart[s - 1];
    for (let i = n - 1; i >= 0; i--) cellItems[--cellStart[cellSlots[i]]] = i;

    // March along each normal line cell by cell, from outside the surface to deep inside.
    const step = SEPARATION_CELL / 2, lateral2 = SEPARATION_CELL * SEPARATION_CELL;
    const phase = this.separationPhase = (this.separationPhase + 1) % SEPARATION_SWEEP;
    /** Walls and gaps found: vertex and signed distance to halfway (outward for a gap). */
    const found: number[] = [];
    // 1: measured now; 2: the far side of a wall or gap under the brush, measured after.
    const due = new Uint8Array(n);
    for (let i = 0; i < n; i++) {
      due[i] = all || weights[i] !== 0 || thickness[i] < SEPARATE_BELOW * MIN_THICKNESS ||
          gap[i] < SEPARATE_BELOW * MIN_GAP || i % SEPARATION_SWEEP === phase
        ? 1
        : 0;
    }
    const measure = (i: number) => {
      const o = i * 3;
      const px = rest[o], py = rest[o + 1], pz = rest[o + 2];
      const nx = nr[o], ny = nr[o + 1], nz = nr[o + 2];
      let wall = THICK_ENOUGH, open = WIDE_ENOUGH, last = -1, across = -1, beyond = -1;
      for (let d = -WIDE_ENOUGH; d < THICK_ENOUGH + step; d += step) {
        const s = cellSlot(px - nx * d, py - ny * d, pz - nz * d, mask);
        if (s === last) continue;
        last = s;
        for (let e = cellStart[s]; e < cellStart[s + 1]; e++) {
          const q = cellItems[e] * 3;
          if (nx * nr[q] + ny * nr[q + 1] + nz * nr[q + 2] > OPPOSED) continue;
          const dx = px - rest[q], dy = py - rest[q + 1], dz = pz - rest[q + 2];
          // Depth of the other vertex below this one: through the clay if positive, across
          // a gap if negative. Either way this vertex must lie the same way from it.
          const t = dx * nx + dy * ny + dz * nz;
          if (dx * dx + dy * dy + dz * dz - t * t > lateral2) continue;
          const back = dx * nr[q] + dy * nr[q + 1] + dz * nr[q + 2];
          if (t > 0 && t < wall && back < 0) {
            wall = t;
            across = q / 3;
          } else if (t < 0 && -t < open && back > 0) {
            open = -t;
            beyond = q / 3;
          }
        }
      }
      // Until checked again, a crossing or inside-out stretch stays so.
      thickness[i] = thickness[i] < 0 && wall < THICK_ENOUGH ? -wall : wall;
      gap[i] = gap[i] < 0 && open < WIDE_ENOUGH ? -open : open;
      if (wall < THICK_ENOUGH) found.push(i, -wall / 2);
      if (open < WIDE_ENOUGH) found.push(i, open / 2);
      if (weights[i] !== 0) {
        if (across >= 0 && !due[across]) due[across] = 2;
        if (beyond >= 0 && !due[beyond]) due[beyond] = 2;
      }
    };
    for (let i = 0; i < n; i++) if (due[i] === 1) measure(i);
    for (let i = 0; i < n; i++) if (due[i] === 2) measure(i);

    // Tell crossings and inside-out clay from real walls and gaps by the point halfway.
    const count = found.length / 2;
    if (count > 0) {
      const { inside } = this;
      inside.build(rest, this.index, SEPARATION_CELL);
      const first = this.insidePhase % count, tests = Math.min(count, INSIDE_TESTS);
      this.insidePhase = first + tests;
      for (let m = first; m < first + tests; m++) {
        const k = (m % count) * 2;
        const i = found[k], o = i * 3, h = found[k + 1];
        const within = inside.contains(
          rest[o] + nr[o] * h,
          rest[o + 1] + nr[o + 1] * h,
          rest[o + 2] + nr[o + 2] * h,
        );
        if (h > 0) gap[i] = within ? -h * 2 : h * 2;
        else thickness[i] = within ? -h * 2 : h * 2;
      }
    }
    this.updateRoom();
  }

  /** Distances to every surface crossing along a ray (unit direction) through the clay. */
  private rayCrossings(
    x: number,
    y: number,
    z: number,
    dx: number,
    dy: number,
    dz: number,
  ): number[] {
    const { rest: p, index } = this;
    const crossings: number[] = [];
    for (let t = 0; t < index.length; t += 3) {
      const ia = index[t] * 3, ib = index[t + 1] * 3, ic = index[t + 2] * 3;
      const ax = p[ia], ay = p[ia + 1], az = p[ia + 2];
      const e1x = p[ib] - ax, e1y = p[ib + 1] - ay, e1z = p[ib + 2] - az;
      const e2x = p[ic] - ax, e2y = p[ic + 1] - ay, e2z = p[ic + 2] - az;
      const px = dy * e2z - dz * e2y, py = dz * e2x - dx * e2z, pz = dx * e2y - dy * e2x;
      const det = e1x * px + e1y * py + e1z * pz;
      if (Math.abs(det) < 1e-12) continue;
      const inv = 1 / det;
      const sx = x - ax, sy = y - ay, sz = z - az;
      const u = (sx * px + sy * py + sz * pz) * inv;
      if (u < 0 || u > 1) continue;
      const qx = sy * e1z - sz * e1y, qy = sz * e1x - sx * e1z, qz = sx * e1y - sy * e1x;
      const v = (dx * qx + dy * qy + dz * qz) * inv;
      if (v < 0 || u + v > 1) continue;
      const d = (e2x * qx + e2y * qy + e2z * qz) * inv;
      if (d > 0) crossings.push(d);
    }
    return crossings;
  }

  /**
   * How freely each vertex may move inward (`roomIn`) and outward (`roomOut`): 0 at the
   * thinnest wall or narrowest gap allowed, 1 when thick or wide enough. Averaged over a
   * couple of rings: facing sides are found vertex by vertex, and a patchy brake on
   * pressing and refilling would wrinkle the surface.
   */
  private updateRoom(): void {
    const { thickness, gap, roomIn, roomOut } = this;
    const n = this.vertexCount;
    let any = false;
    for (let i = 0; i < n; i++) {
      roomIn[i] = thickness[i] < 0 ? 1 : smoothstep(MIN_THICKNESS, THICK_ENOUGH, thickness[i]);
      roomOut[i] = gap[i] < 0 ? 1 : smoothstep(MIN_GAP, WIDE_ENOUGH, gap[i]);
      if (roomIn[i] < 1 || roomOut[i] < 1) any = true;
    }
    if (!any) return;
    for (let pass = 0; pass < ROOM_SMOOTH_PASSES; pass++) {
      this.averageRings(roomIn);
      this.averageRings(roomOut);
    }
  }

  /** Replaces each value with the mean over the vertex and its one-ring. */
  private averageRings(values: Float32Array): void {
    const { adjOffsets, adjNeighbors, scratch } = this;
    const n = this.vertexCount;
    for (let i = 0; i < n; i++) {
      const start = adjOffsets[i], end = adjOffsets[i + 1];
      let sum = values[i];
      for (let e = start; e < end; e++) sum += values[adjNeighbors[e]];
      scratch[i] = sum / (end - start + 1);
    }
    values.set(scratch.subarray(0, n));
  }

  /**
   * Pushes both sides of walls well thinner than MIN_THICKNESS outward, and both sides of
   * gaps well narrower than MIN_GAP back in (see SEPARATE_BELOW); untangles surfaces that
   * passed through each other (see measureSeparation). The shortfall is averaged
   * with the one-ring first, which fills in vertices whose facing side was not found, and
   * the pushed vertices are smoothed afterwards.
   */
  private separate(): void {
    const { rest, restNormals: nr, thickness, gap, adjOffsets, adjNeighbors, scratch } = this;
    const { roomIn, roomOut, flagged } = this;
    const n = this.vertexCount;
    let any = false;
    for (let i = 0; i < n; i++) {
      const t = thickness[i], g = gap[i];
      const thin = t < 0 ? 0 : Math.max(0, SEPARATE_BELOW * MIN_THICKNESS - t);
      const shut = g < 0 ? 0 : Math.max(0, SEPARATE_BELOW * MIN_GAP - g);
      // Crossings retreat back through; walls pushed through push back out.
      const back = t < 0 ? MIN_GAP - t : 0, out = g < 0 ? MIN_THICKNESS - g : 0;
      // A side with no room to go (a thin flap lying in a narrow gap) leaves the push to
      // the other side of the wall or gap.
      scratch[i] = thin * roomOut[i] - shut * roomIn[i] - back + out;
      if (thin > 0 || shut > 0 || back > 0 || out > 0) any = true;
    }
    if (!any) return;
    const cap = MAX_SEPARATE_STEP * EDGE_LENGTH;
    for (let i = 0; i < n; i++) {
      const start = adjOffsets[i], end = adjOffsets[i + 1];
      let sum = 0;
      for (let e = start; e < end; e++) sum += scratch[adjNeighbors[e]];
      // Each side covers half of the shortfall.
      const m = 0.5 * (scratch[i] + sum / (end - start)) * 0.5 * SEPARATE_RATE;
      scratch[n + i] = Math.max(-cap, Math.min(cap, m));
    }
    let count = 0;
    for (let i = 0; i < n; i++) {
      const m = scratch[n + i];
      if (m === 0) continue;
      rest[i * 3] += nr[i * 3] * m;
      rest[i * 3 + 1] += nr[i * 3 + 1] * m;
      rest[i * 3 + 2] += nr[i * 3 + 2] * m;
      flagged[count++] = i;
    }
    const avg = [0, 0, 0];
    for (let k = 0; k < count; k++) {
      const o = flagged[k] * 3;
      this.ringAverage(flagged[k], avg);
      scratch[k * 3] = rest[o] + (avg[0] - rest[o]) * SEPARATE_SMOOTH;
      scratch[k * 3 + 1] = rest[o + 1] + (avg[1] - rest[o + 1]) * SEPARATE_SMOOTH;
      scratch[k * 3 + 2] = rest[o + 2] + (avg[2] - rest[o + 2]) * SEPARATE_SMOOTH;
    }
    for (let k = 0; k < count; k++) {
      const o = flagged[k] * 3;
      rest[o] = scratch[k * 3];
      rest[o + 1] = scratch[k * 3 + 1];
      rest[o + 2] = scratch[k * 3 + 2];
    }
  }

  /**
   * Slides vertices along the surface toward their ring centroid, which evens out
   * stretched triangles without changing the shape; strongest inside the brush.
   * `rounding` (0–1) also lets brush vertices move across the surface, smoothing bumps
   * and sharp tips, though not into a thin wall or narrow gap (which rounding a thin
   * tube would otherwise do, shrinking it).
   */
  private relax(rounding: number): void {
    const { rest, restNormals: nr, weights, roomIn, roomOut, scratch } = this;
    const avg = [0, 0, 0];
    for (let i = 0; i < this.vertexCount; i++) {
      const o = i * 3, w = weights[i];
      this.ringAverage(i, avg);
      let lx = avg[0] - rest[o], ly = avg[1] - rest[o + 1], lz = avg[2] - rest[o + 2];
      const along = lx * nr[o] + ly * nr[o + 1] + lz * nr[o + 2];
      const ln = along * (1 - rounding * w * (along < 0 ? roomIn[i] : roomOut[i]));
      lx -= ln * nr[o];
      ly -= ln * nr[o + 1];
      lz -= ln * nr[o + 2];
      const s = Math.max(RELAX_GLOBAL, RELAX * Math.sqrt(w));
      scratch[o] = rest[o] + lx * s;
      scratch[o + 1] = rest[o + 1] + ly * s;
      scratch[o + 2] = rest[o + 2] + lz * s;
    }
    rest.set(scratch);
  }

  /**
   * Finds spikes and folded (flipped) triangles and smooths them out together with
   * their one-ring. Large pulls and noisy normals otherwise let them grow frame by frame.
   */
  private repair(): void {
    const { rest, index, restNormals: nr, adjOffsets, adjNeighbors, flags, flagged, scratch } =
      this;
    computeStats(rest, index, nr, this.restAreas);
    flags.fill(0);

    for (let t = 0; t < index.length; t += 3) {
      const ia = index[t] * 3, ib = index[t + 1] * 3, ic = index[t + 2] * 3;
      const e1x = rest[ib] - rest[ia], e1y = rest[ib + 1] - rest[ia + 1];
      const e1z = rest[ib + 2] - rest[ia + 2];
      const e2x = rest[ic] - rest[ia], e2y = rest[ic + 1] - rest[ia + 1];
      const e2z = rest[ic + 2] - rest[ia + 2];
      const fx = e1y * e2z - e1z * e2y, fy = e1z * e2x - e1x * e2z, fz = e1x * e2y - e1y * e2x;
      const dot = fx * (nr[ia] + nr[ib] + nr[ic]) + fy * (nr[ia + 1] + nr[ib + 1] + nr[ic + 1]) +
        fz * (nr[ia + 2] + nr[ib + 2] + nr[ic + 2]);
      if (dot < 0) flags[ia / 3] = flags[ib / 3] = flags[ic / 3] = 1;
    }

    const avg = [0, 0, 0];
    for (let i = 0; i < this.vertexCount; i++) {
      const o = i * 3;
      const start = adjOffsets[i], end = adjOffsets[i + 1];
      let edge = 0;
      for (let e = start; e < end; e++) {
        const j = adjNeighbors[e] * 3;
        const dx = rest[j] - rest[o],
          dy = rest[j + 1] - rest[o + 1],
          dz = rest[j + 2] - rest[o + 2];
        edge += Math.sqrt(dx * dx + dy * dy + dz * dz);
      }
      this.ringAverage(i, avg);
      const h = (avg[0] - rest[o]) * nr[o] + (avg[1] - rest[o + 1]) * nr[o + 1] +
        (avg[2] - rest[o + 2]) * nr[o + 2];
      if (Math.abs(h) > SPIKE_RATIO * edge / (end - start)) flags[i] = 1;
    }

    let count = 0;
    for (let i = 0; i < this.vertexCount; i++) {
      if (flags[i] !== 1) continue;
      flagged[count++] = i;
    }
    const core = count;
    for (let k = 0; k < core; k++) {
      const i = flagged[k];
      for (let e = adjOffsets[i]; e < adjOffsets[i + 1]; e++) {
        const j = adjNeighbors[e];
        if (flags[j] === 0) {
          flags[j] = 2;
          flagged[count++] = j;
        }
      }
    }
    if (count === 0) return;

    for (let pass = 0; pass < REPAIR_PASSES; pass++) {
      for (let k = 0; k < count; k++) {
        const i = flagged[k], o = i * 3;
        this.ringAverage(i, avg);
        const s = flags[i] === 1 ? 1 : 0.5;
        scratch[k * 3] = rest[o] + (avg[0] - rest[o]) * s;
        scratch[k * 3 + 1] = rest[o + 1] + (avg[1] - rest[o + 1]) * s;
        scratch[k * 3 + 2] = rest[o + 2] + (avg[2] - rest[o + 2]) * s;
      }
      for (let k = 0; k < count; k++) {
        const o = flagged[k] * 3;
        rest[o] = scratch[k * 3];
        rest[o + 1] = scratch[k * 3 + 1];
        rest[o + 2] = scratch[k * 3 + 2];
      }
    }
  }

  /**
   * Laplacian smoothing under the brush. Its move along the normal slows into a thin wall
   * or across a narrow gap, as the tools' do: the sides of a narrow dent would otherwise
   * draw together until held at MIN_GAP, leaving a navel no smoothing fills, where braked
   * they stay apart while the bottom comes up.
   */
  private smooth(amount: number): void {
    const { rest, restNormals: nr, weights, active, scratch, roomIn, roomOut } = this;
    const avg = [0, 0, 0];
    for (let k = 0; k < this.activeCount; k++) {
      const i = active[k], o = i * 3;
      this.ringAverage(i, avg);
      const s = amount * weights[i];
      let lx = avg[0] - rest[o], ly = avg[1] - rest[o + 1], lz = avg[2] - rest[o + 2];
      const along = lx * nr[o] + ly * nr[o + 1] + lz * nr[o + 2];
      const held = along * (1 - (along < 0 ? roomIn[i] : roomOut[i]));
      lx -= held * nr[o];
      ly -= held * nr[o + 1];
      lz -= held * nr[o + 2];
      scratch[k * 3] = rest[o] + lx * s;
      scratch[k * 3 + 1] = rest[o + 1] + ly * s;
      scratch[k * 3 + 2] = rest[o + 2] + lz * s;
    }
    this.commitScratch();
  }

  private commitScratch(): void {
    const { rest, active, scratch } = this;
    for (let k = 0; k < this.activeCount; k++) {
      const o = active[k] * 3;
      rest[o] = scratch[k * 3];
      rest[o + 1] = scratch[k * 3 + 1];
      rest[o + 2] = scratch[k * 3 + 2];
    }
  }

  /**
   * Restores the conserved volume by moving vertices along their normals, as `refill`
   * says: "body" spreads the correction over everything outside the brush; points
   * concentrate it around them (a rim forms around a dent); "brush" keeps it inside the
   * brush, so smoothing evens out a neck or crease instead of thinning it.
   * Shrinking spares thin parts, so they do not wither while clay is added elsewhere, and
   * swelling spares narrow gaps. What the refill cannot take comes back out of the brush
   * itself: the stroke then stops adding (or removing) clay.
   */
  private preserveVolume(refill: Refill): void {
    const { rest, restNormals: nr, restAreas, weights, scratch } = this;
    const n = this.vertexCount;
    const stats = computeStats(rest, this.index, nr, restAreas);
    this.restStats = stats;
    const shrinking = stats.volume > CLAY_VOLUME;
    const room = shrinking ? this.roomIn : this.roomOut;
    const rimRadius = this.brushRadius * 2.5;
    let denom = 0;
    for (let i = 0; i < n; i++) {
      let m = refill === "brush" ? weights[i] : 1 - weights[i];
      if (Array.isArray(refill)) {
        _p.fromArray(rest, i * 3);
        let d = Infinity;
        for (const c of refill) d = Math.min(d, _p.distanceTo(c));
        m *= 0.2 + falloff(d / rimRadius);
      }
      m *= room[i];
      scratch[i] = m;
      denom += restAreas[i] * m;
    }
    let missing = CLAY_VOLUME - stats.volume;
    if (denom > 1e-9) {
      const c = missing / denom, cap = MAX_REFILL_STEP * EDGE_LENGTH;
      for (let i = 0; i < n; i++) {
        const s = Math.max(-cap, Math.min(cap, c * scratch[i]));
        rest[i * 3] += nr[i * 3] * s;
        rest[i * 3 + 1] += nr[i * 3 + 1] * s;
        rest[i * 3 + 2] += nr[i * 3 + 2] * s;
        missing -= restAreas[i] * s;
        scratch[i] = s;
      }
      // Moved along their own normals, the refilled vertices would wrinkle up step by step
      // (nothing rounds the clay outside the brush): each is rounded toward its one-ring
      // across the surface, as far as it was moved.
      const avg = [0, 0, 0];
      for (let i = 0; i < n; i++) {
        const o = i * 3;
        this.ringAverage(i, avg);
        const h = (avg[0] - rest[o]) * nr[o] + (avg[1] - rest[o + 1]) * nr[o + 1] +
          (avg[2] - rest[o + 2]) * nr[o + 2];
        scratch[n + i] = h * Math.min(1, REFILL_ROUNDING * Math.abs(scratch[i]) / EDGE_LENGTH);
      }
      for (let i = 0; i < n; i++) {
        const o = i * 3, h = scratch[n + i];
        rest[o] += nr[o] * h;
        rest[o + 1] += nr[o + 1] * h;
        rest[o + 2] += nr[o + 2] * h;
      }
    }
    if (refill === "brush" || Math.abs(missing) < 1e-9) return;
    denom = 0;
    for (let i = 0; i < n; i++) {
      scratch[i] = weights[i] * room[i];
      denom += restAreas[i] * scratch[i];
    }
    if (denom < 1e-9) return;
    const c = missing / denom;
    for (let i = 0; i < n; i++) {
      const s = c * scratch[i];
      rest[i * 3] += nr[i * 3] * s;
      rest[i * 3 + 1] += nr[i * 3 + 1] * s;
      rest[i * 3 + 2] += nr[i * 3 + 2] * s;
    }
  }

  /** Keeps the clay centred on the origin (the camera pivot) after each stroke. */
  private recenter(): void {
    const stats = computeStats(this.rest, this.index, this.restNormals, this.restAreas);
    const c = stats.centroid;
    const rest = this.rest;
    for (let i = 0; i < rest.length; i += 3) {
      rest[i] -= c.x;
      rest[i + 1] -= c.y;
      rest[i + 2] -= c.z;
    }
    c.set(0, 0, 0);
    this.restStats = stats;
    this.version++;
    this.awake = true;
  }
}
